/**
 * Order sheet — inline product details accordion (media, variant table, add selected, weight modal).
 */
(function () {
  'use strict';

  const FILTER_EVT = 'filter:update';
  const WEIGHT_SEARCH_DEBOUNCE_MS = 150;
  const WEIGHT_FETCH_TIMEOUT_MS = 15000;
  const WEIGHT_MODAL_FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="68" height="68" viewBox="0 0 68 68"><rect width="68" height="68" rx="8" fill="#f3f4f6"/><path d="M16 47l11-12 9 9 7-7 9 10" fill="none" stroke="#9ca3af" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="26" cy="24" r="4" fill="#d1d5db"/></svg>'
    );
  const TOGGLE_SEL = '[data-order-sheet-details-toggle], [data-plp-details-toggle]';
  const ITEM_SEL = '[data-order-sheet-item], [data-plp-inventory-item], [data-plp-table-item]';
  const ROW_SEL = '[data-order-sheet-row], [data-product-row].inventory-product-row';
  const PANEL_SEL = '[data-order-sheet-details-panel], [data-plp-details-panel]';

  /** @type {WeakMap<HTMLElement, AbortController>} */
  const hostControllers = new WeakMap();
  /** @type {Record<string, { entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>} */
  const weightCache = Object.create(null);
  /** @type {Record<string, Promise<{ entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>>} */
  const weightPending = Object.create(null);

  let documentToggleBound = false;
  let toastHost = /** @type {HTMLElement | null} */ (null);

  /** @param {string} s */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** @param {string} message @param {'error'|'info'} [type] */
  function showToast(message, type) {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'order-sheet-weight-toast-host';
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    const el = document.createElement('div');
    el.className = 'order-sheet-weight-toast' + (type === 'error' ? ' order-sheet-weight-toast--error' : '');
    el.textContent = message;
    toastHost.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    window.setTimeout(() => {
      el.classList.remove('is-visible');
      window.setTimeout(() => el.remove(), 300);
    }, 4200);
  }

  /** @param {Element | null | undefined} cfgEl */
  function parseWeightConfigElement(cfgEl) {
    const defaults = { apiBaseUrl: '', weightsPath: '/public/products' };
    if (!cfgEl || !cfgEl.textContent) return defaults;
    try {
      const parsed = JSON.parse(cfgEl.textContent);
      const apiBaseUrl = String(
        (parsed && (parsed.apiBaseUrl || parsed.baseUrl)) || ''
      )
        .trim()
        .replace(/\/+$/, '');
      return {
        apiBaseUrl,
        weightsPath:
          parsed && parsed.weightsPath
            ? String(parsed.weightsPath).replace(/\/+$/, '')
            : '/public/products',
      };
    } catch {
      return defaults;
    }
  }

  /** @param {HTMLElement} root */
  function getFetchWeightConfig(root) {
    /** @type {Array<Element | null | undefined>} */
    const candidates = [
      root.querySelector('[data-plp-fetch-weight-config], [data-quick-view-weight-config]'),
      root.closest('[data-plp-expanded-details]')?.querySelector(
        '[data-plp-fetch-weight-config], [data-quick-view-weight-config]'
      ),
      root.closest('[data-quick-view-root], #QuickViewModal')?.querySelector(
        '[data-plp-fetch-weight-config], [data-quick-view-weight-config]'
      ),
      root.closest('[data-pdp-section]')?.querySelector('[data-pdp-fetch-weight-config]'),
      document.querySelector('#QuickViewModal [data-plp-fetch-weight-config], #QuickViewModal [data-quick-view-weight-config]'),
      document.querySelector('[data-plp-fetch-weight-config-section]'),
      document.querySelector('[data-batch-weight-config]'),
    ];

    /** @type {{ apiBaseUrl: string, weightsPath: string }} */
    let resolved = { apiBaseUrl: '', weightsPath: '/public/products' };
    for (const el of candidates) {
      if (el && el.hasAttribute('data-batch-weight-config')) {
        try {
          const raw = JSON.parse(el.textContent || '{}');
          if (raw && raw.enabled === false) continue;
        } catch {
          /* ignore */
        }
      }
      const cfg = parseWeightConfigElement(el);
      if (cfg.weightsPath) resolved.weightsPath = cfg.weightsPath;
      if (cfg.apiBaseUrl) {
        resolved = cfg;
        break;
      }
    }
    return resolved;
  }

  /** @param {string} base @param {string} path */
  function joinUrl(base, path) {
    const b = String(base || '').replace(/\/+$/, '');
    let p = String(path || '');
    if (!p) return b || '';
    if (/^https?:\/\//i.test(p)) return p;
    if (p.charAt(0) !== '/') p = '/' + p;
    return b ? b + p : p;
  }

  /** @param {string} url */
  function getWeightRequestHeaders(url) {
    /** @type {Record<string, string>} */
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    try {
      const parsed = new URL(url, window.location.origin);
      const host = (parsed.hostname || '').toLowerCase();
      if (host.indexOf('ngrok') >= 0) headers['ngrok-skip-browser-warning'] = 'true';
    } catch {
      /* ignore */
    }
    return headers;
  }

  /**
   * Shared weight cache hydrated by weight-service products-batch (via quick-view sync).
   * @returns {{
   *   rawBySku?: Record<string, unknown>,
   *   dataBySku?: Record<string, { entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>,
   *   pendingBySku?: Record<string, Promise<void>>
   * } | null}
   */
  function getSharedWeightStore() {
    const w = /** @type {any} */ (window);
    return w.__aoneWeightSharedStore || null;
  }

  /**
   * Wait for the in-flight products-batch request. Never starts a new weight API call.
   * @returns {Promise<void>}
   */
  async function waitForCurrentWeightData() {
    const w = /** @type {any} */ (window);
    const api = w.AOneProductWeightsAPI || w.AoneBatchWeight;
    if (api && typeof api.waitForCurrentWeightData === 'function') {
      await api.waitForCurrentWeightData();
      return;
    }
    if (w.AOneProductWeights?.activeRequest) {
      await w.AOneProductWeights.activeRequest;
    }
  }

  /**
   * Look up a SKU item from the product-level in-memory cache.
   * @param {string} sku
   * @returns {Record<string, unknown> | null}
   */
  function getCachedSkuItem(sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return null;
    const store = /** @type {any} */ (window).AOneProductWeights;
    if (!store?.skus) return null;
    const entry =
      store.skus.get(safeSku) ||
      store.skus.get(safeSku.toUpperCase()) ||
      store.skus.get(safeSku.toLowerCase());
    if (entry?.item && typeof entry.item === 'object') {
      return /** @type {Record<string, unknown>} */ (entry.item);
    }
    return null;
  }

  /** @param {string} sku @param {{ entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }} normalized */
  function setWeightCacheForSku(sku, normalized) {
    const safeSku = String(sku || '').trim();
    if (!safeSku || !normalized || !Array.isArray(normalized.entries)) return;
    weightCache[safeSku] = normalized;
    const shared = getSharedWeightStore();
    if (shared && shared.dataBySku) {
      shared.dataBySku[safeSku.toLowerCase()] = normalized;
    }
  }

  /** @param {string} sku */
  function readSharedWeightForSku(sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return null;
    const shared = getSharedWeightStore();
    if (!shared) return null;
    const key = safeSku.toLowerCase();

    const normalized = shared.dataBySku?.[key];
    if (normalized && Array.isArray(normalized.entries) && normalized.entries.length) {
      return normalized;
    }

    const raw = shared.rawBySku?.[key];
    if (raw === undefined) return null;
    const entries = extractWeightEntries(raw);
    if (!entries.length) return null;
    const fromRaw = { entries };
    if (shared.dataBySku) shared.dataBySku[key] = fromRaw;
    return fromRaw;
  }

  /** @param {string} key */
  function weightFieldLabel(key) {
    return String(key || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim();
  }

  /** @param {string} key @param {unknown} value */
  function formatWeightFieldValue(key, value) {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number' && Number.isFinite(value)) {
      const k = String(key || '').toLowerCase();
      if (k.indexOf('weight') >= 0 || k.indexOf('gram') >= 0) return value.toFixed(2) + ' g';
      return String(value);
    }
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    const k = String(key || '').toLowerCase();
    if (k.indexOf('updated') >= 0 || k.indexOf('date') >= 0 || k.indexOf('time') >= 0) {
      const dt = new Date(trimmed);
      if (!Number.isNaN(dt.getTime())) {
        try {
          return dt.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return dt.toISOString();
        }
      }
    }
    return trimmed;
  }

  /** @param {object | number | string} entry @param {number} idx */
  function normalizeWeightEntry(entry, idx) {
    let id = 'weight-' + idx;
    /** @type {Array<{ label: string, value: string }>} */
    const fields = [];
    let label = '';

    if (typeof entry === 'number' || typeof entry === 'string') {
      const simpleValue = formatWeightFieldValue('weight', entry);
      if (!simpleValue) return null;
      fields.push({ label: 'Weight', value: simpleValue });
      return { id, label: simpleValue, fields };
    }

    if (!entry || typeof entry !== 'object') return null;
    const obj = /** @type {Record<string, unknown>} */ (entry);
    if (obj.id != null) id = String(obj.id);
    if (obj.weight_id != null) id = String(obj.weight_id);

    const serialNumberRaw =
      obj.serialNumber != null
        ? obj.serialNumber
        : obj.serial_number != null
          ? obj.serial_number
          : null;
    const serialNumber =
      serialNumberRaw === null || serialNumberRaw === undefined ? '' : String(serialNumberRaw).trim();

    if (serialNumber && obj.weight != null) {
      const weightNum = Number(obj.weight);
      const weightLabel = formatWeightFieldValue('weight', obj.weight);
      if (!weightLabel || !Number.isFinite(weightNum) || weightNum <= 0) return null;
      return {
        id: serialNumber,
        label: weightLabel,
        serialNumber,
        weightGrams: weightNum,
        unit: obj.unit != null ? String(obj.unit) : 'g',
        fields: [
          { label: 'Serial', value: serialNumber },
          { label: 'Weight', value: weightLabel },
        ],
      };
    }

    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const raw = obj[key];
      if (raw == null || typeof raw === 'object') continue;
      const formatted = formatWeightFieldValue(key, raw);
      if (!formatted) continue;
      fields.push({ label: weightFieldLabel(key), value: formatted });
    }

    if (!fields.length) return null;
    const preferred = [
      'gross_weight',
      'gross_weight_g',
      'net_weight',
      'net_weight_g',
      'metal_weight',
      'diamond_weight',
      'weight',
      'weight_g',
    ];
    for (const pref of preferred) {
      const prefLabel = weightFieldLabel(pref);
      const match = fields.find((f) => f.label === prefLabel);
      if (match) {
        label = match.value;
        break;
      }
    }
    if (!label) label = fields[0].value || 'Weight #' + (idx + 1);
    return { id, label, fields };
  }

  /**
   * Prefer availableOptions (serial + weight) over availableWeights (numbers only).
   * App sync requires real WMS serial numbers; plain weights produce fake ids like "weight-0".
   * @param {Record<string, unknown>} obj
   * @returns {Array<object | number | string> | null}
   */
  function pickWeightEntryList(obj) {
    if (Array.isArray(obj.availableOptions)) return obj.availableOptions;
    if (Array.isArray(obj.availableWeights)) return obj.availableWeights;
    if (Array.isArray(obj.weights)) return obj.weights;
    return null;
  }

  /** @param {unknown} payload @param {{ skipReservedFilter?: boolean }} [options] */
  function extractWeightEntries(payload, options) {
    const opts = options || {};
    /** @type {Array<object | number | string>} */
    let rawEntries = [];
    if (Array.isArray(payload)) {
      rawEntries = payload;
    } else if (payload && typeof payload === 'object') {
      const root = /** @type {Record<string, unknown>} */ (payload);
      const rootList = pickWeightEntryList(root);
      if (rootList) rawEntries = rootList;
      else if (Array.isArray(root.data)) rawEntries = root.data;
      else if (Array.isArray(root.items)) rawEntries = root.items;
      else if (Array.isArray(root.results)) rawEntries = root.results;
      else if (root.data && typeof root.data === 'object') {
        const data = /** @type {Record<string, unknown>} */ (root.data);
        const dataList = pickWeightEntryList(data);
        if (dataList) rawEntries = dataList;
      } else {
        rawEntries = [root];
      }
    }

    /** @type {Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }>} */
    const entries = [];
    const inventoryApi = getWmsAvailabilityApi();
    for (let i = 0; i < rawEntries.length; i++) {
      const normalized = normalizeWeightEntry(rawEntries[i], i);
      if (!normalized) continue;
      const serial = String(normalized.serialNumber || '').trim();
      if (
        !opts.skipReservedFilter &&
        serial &&
        inventoryApi?.isSerialReserved?.(serial)
      ) {
        continue;
      }
      entries.push(normalized);
    }
    return entries;
  }

  /**
   * @param {Record<string, unknown>} obj
   * @returns {string}
   */
  function extractSkuFromObject(obj) {
    const keys = [
      'sku',
      'SKU',
      'productSku',
      'product_sku',
      'variantSku',
      'variant_sku',
      'itemCode',
      'item_code',
      'code',
    ];
    for (const key of keys) {
      if (!(key in obj)) continue;
      const sku = String(obj[key] || '').trim();
      if (sku) return sku;
    }
    return '';
  }

  /**
   * Extract node for a single SKU from batch payload shapes.
   * @param {unknown} payload
   * @param {string} targetSku
   * @returns {unknown | null}
   */
  function extractBatchNodeForSku(payload, targetSku) {
    const needle = String(targetSku || '').trim().toLowerCase();
    if (!needle || !payload || typeof payload !== 'object') return null;

    /**
     * @param {unknown} node
     * @returns {unknown | null}
     */
    const fromList = (node) => {
      if (!Array.isArray(node)) return null;
      for (const item of node) {
        if (!item || typeof item !== 'object') continue;
        const obj = /** @type {Record<string, unknown>} */ (item);
        const sku = extractSkuFromObject(obj).toLowerCase();
        if (sku === needle) return obj;
      }
      return null;
    };

    const root = /** @type {Record<string, unknown>} */ (payload);
    const rootByList = fromList(root);
    if (rootByList) return rootByList;

    for (const key of ['products', 'results', 'items', 'data', 'weights']) {
      const bucket = root[key];
      if (!bucket) continue;

      const matchFromList = fromList(bucket);
      if (matchFromList) return matchFromList;

      if (bucket && typeof bucket === 'object' && !Array.isArray(bucket)) {
        const obj = /** @type {Record<string, unknown>} */ (bucket);
        const sku = extractSkuFromObject(obj).toLowerCase();
        if (sku === needle) return obj;

        for (const [k, v] of Object.entries(obj)) {
          if (String(k).trim().toLowerCase() === needle) return v;
        }

        const nestedItems = obj.items;
        const matchNested = fromList(nestedItems);
        if (matchNested) return matchNested;
      }
    }

    for (const [k, v] of Object.entries(root)) {
      if (String(k).trim().toLowerCase() === needle) return v;
    }
    return null;
  }

  /** @param {HTMLElement} root @param {string} sku */
  function fetchWeightPayload(root, sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return Promise.reject(new Error('SKU missing'));
    if (weightCache[safeSku]) return Promise.resolve(weightCache[safeSku]);
    if (weightPending[safeSku]) return weightPending[safeSku];

    const sharedImmediate = readSharedWeightForSku(safeSku);
    if (sharedImmediate) {
      setWeightCacheForSku(safeSku, sharedImmediate);
      return Promise.resolve(sharedImmediate);
    }

    const fromProductCache = getCachedSkuItem(safeSku);
    if (fromProductCache) {
      const entries = extractWeightEntries(fromProductCache);
      const normalized = { entries };
      setWeightCacheForSku(safeSku, normalized);
      const shared = getSharedWeightStore();
      if (shared?.rawBySku) {
        shared.rawBySku[safeSku.toLowerCase()] = fromProductCache;
      }
      return Promise.resolve(normalized);
    }

    const WEIGHT_UNAVAILABLE_MSG =
      'Weight information is currently unavailable. Please try again.';

    /**
     * Resolve from in-memory cache only after any in-flight products-batch completes.
     * Never calls /weights/batch or /weights/products-batch from weight selection.
     */
    const resolveFromCacheOnly = () =>
      waitForCurrentWeightData()
        .then(() => {
          const fromShared = readSharedWeightForSku(safeSku);
          if (fromShared) {
            setWeightCacheForSku(safeSku, fromShared);
            return fromShared;
          }

          const item = getCachedSkuItem(safeSku);
          if (item) {
            const entries = extractWeightEntries(item);
            const normalized = { entries };
            setWeightCacheForSku(safeSku, normalized);
            const shared = getSharedWeightStore();
            if (shared?.rawBySku) {
              shared.rawBySku[safeSku.toLowerCase()] = item;
            }
            return normalized;
          }

          throw new Error(WEIGHT_UNAVAILABLE_MSG);
        })
        .catch((err) => {
          const message =
            err && typeof err.message === 'string' && err.message
              ? err.message
              : WEIGHT_UNAVAILABLE_MSG;
          throw new Error(message);
        });

    const shared = getSharedWeightStore();
    const sharedPending = shared?.pendingBySku?.[safeSku.toLowerCase()] || null;
    const req = (sharedPending
      ? Promise.resolve(sharedPending).then(() => {
          const fromShared = readSharedWeightForSku(safeSku);
          if (fromShared) {
            setWeightCacheForSku(safeSku, fromShared);
            return fromShared;
          }
          return resolveFromCacheOnly();
        })
      : resolveFromCacheOnly()
    ).finally(() => {
      delete weightPending[safeSku];
    });

    weightPending[safeSku] = req;
    return req;
  }

  /** @param {HTMLElement} panel */
  function clearCloseTimer(panel) {
    const id = panel.dataset.closeTimerId;
    if (id) {
      window.clearTimeout(Number(id));
      delete panel.dataset.closeTimerId;
    }
  }

  /** @param {HTMLElement} host */
  function allowMultiple(host) {
    return host.dataset.plpDetailsMulti === 'true';
  }

  /** @param {HTMLInputElement} input */
  function readQty(input) {
    const raw = String(input.value ?? '').trim();
    if (raw === '') return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /**
   * Optional per-variant inventory ceiling from data-variant-inventory-qty / input.max.
   * @param {HTMLElement} row
   * @param {HTMLInputElement} [input]
   * @returns {number | null}
   */
  function getRowQtyMax(row, input) {
    const fromRow = Number(row.dataset.variantInventoryQty);
    if (Number.isFinite(fromRow) && fromRow >= 0) return fromRow;
    if (input) {
      const maxAttr = input.getAttribute('max');
      if (maxAttr != null && maxAttr !== '') {
        const fromMax = Number(maxAttr);
        if (Number.isFinite(fromMax) && fromMax >= 0) return fromMax;
      }
    }
    return null;
  }

  /**
   * Numeric grams for a normalized weight entry (never string-sort weights).
   * @param {{ weightGrams?: number, label?: string, fields?: Array<{ label: string, value: string }> } | null | undefined} entry
   * @returns {number}
   */
  function parseEntryWeightGrams(entry) {
    if (!entry) return Number.POSITIVE_INFINITY;
    const direct = Number(entry.weightGrams);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const fromLabel = parseWeightGrams(String(entry.label || ''));
    if (fromLabel > 0) return fromLabel;
    const weightField = Array.isArray(entry.fields)
      ? entry.fields.find((f) => /weight/i.test(String(f.label || '')))
      : null;
    if (weightField) {
      const fromField = parseWeightGrams(String(weightField.value || ''));
      if (fromField > 0) return fromField;
    }
    return Number.POSITIVE_INFINITY;
  }

  /**
   * Serials already claimed by other variant rows in the same details root.
   * @param {HTMLElement | null} detailsRoot
   * @param {HTMLElement} currentRow
   * @returns {Set<string>}
   */
  function collectSelectedSerialsOutsideRow(detailsRoot, currentRow) {
    /** @type {Set<string>} */
    const used = new Set();
    if (!(detailsRoot instanceof HTMLElement)) return used;
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || row === currentRow) return;
      String(row.dataset.serialNumbersValue || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((serial) => used.add(serial));
    });
    return used;
  }

  /**
   * Valid selectable WMS options for a SKU: unique serials, valid weights, not cart-reserved,
   * not claimed by other rows. Sorted by numeric weight ascending.
   * @param {string} sku
   * @param {Set<string> | string[]} [excludeSerials]
   * @returns {Array<{ id: string, label: string, serialNumber?: string, weightGrams?: number, unit?: string, fields: Array<{ label: string, value: string }> }>}
   */
  function getSelectableWeightEntriesForSku(sku, excludeSerials) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return [];

    /** @type {Array<{ id: string, label: string, serialNumber?: string, weightGrams?: number, unit?: string, fields: Array<{ label: string, value: string }> }>} */
    let entries = [];
    const inventoryApi = getWmsAvailabilityApi();

    // Prefer shared cart-aware effective options so QV/PDP/Order Sheet stay aligned.
    if (inventoryApi && typeof inventoryApi.filterAvailableOptions === 'function') {
      entries = extractWeightEntries(inventoryApi.filterAvailableOptions(safeSku), {
        skipReservedFilter: true,
      });
    }
    if (!entries.length && inventoryApi && typeof inventoryApi.getAvailableOptions === 'function') {
      entries = extractWeightEntries(inventoryApi.getAvailableOptions(safeSku), {
        skipReservedFilter: true,
      });
    }

    if (!entries.length) {
      const shared = getSharedWeightStore();
      const key = safeSku.toLowerCase();
      const raw = shared?.rawBySku?.[key];
      if (raw != null) {
        entries = extractWeightEntries(raw);
      } else {
        const cached = readSharedWeightForSku(safeSku);
        if (cached?.entries?.length) entries = cached.entries.slice();
      }
    }

    const exclude =
      excludeSerials instanceof Set
        ? excludeSerials
        : new Set(Array.isArray(excludeSerials) ? excludeSerials : []);
    /** @type {Set<string>} */
    const seen = new Set();
    /** @type {typeof entries} */
    const out = [];
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const serial = String(entry?.serialNumber || '').trim();
      if (!serial) continue;
      if (seen.has(serial) || exclude.has(serial)) continue;
      if (inventoryApi && typeof inventoryApi.isSerialReserved === 'function' && inventoryApi.isSerialReserved(serial)) {
        continue;
      }
      const grams = parseEntryWeightGrams(entry);
      if (!Number.isFinite(grams) || grams <= 0 || grams === Number.POSITIVE_INFINITY) continue;
      seen.add(serial);
      out.push(entry);
    }
    out.sort((a, b) => {
      const weightDiff = parseEntryWeightGrams(a) - parseEntryWeightGrams(b);
      if (weightDiff !== 0) return weightDiff;
      return String(a.serialNumber || '').localeCompare(String(b.serialNumber || ''));
    });
    return out;
  }

  /**
   * Effective qty ceiling: min(WMS inventory attribute, currently selectable option count).
   * @param {HTMLElement} row
   * @param {HTMLInputElement} [input]
   * @returns {number | null}
   */
  function getRowSelectableMax(row, input) {
    const inventoryMax = getRowQtyMax(row, input);
    const sku = getRowSku(row);
    if (!sku) return inventoryMax;
    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]');
    const exclude =
      detailsRoot instanceof HTMLElement
        ? collectSelectedSerialsOutsideRow(detailsRoot, row)
        : new Set();
    const optionMax = getSelectableWeightEntriesForSku(sku, exclude).length;
    if (inventoryMax == null) return optionMax;
    return Math.min(inventoryMax, optionMax);
  }

  /**
   * Centralized quantity → weight reconciliation for a variant row.
   * Keeps quantity === selected serial count for valid SKU rows.
   * @param {HTMLElement} row
   * @param {number} requestedQty
   * @param {{ silent?: boolean, skipNotify?: boolean }} [opts]
   * @returns {number}
   */
  function reconcileRowQuantitySelection(row, requestedQty, opts = {}) {
    const input = getRowQtyInput(row);
    if (!(row instanceof HTMLElement) || !input) return 0;

    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]');
    const panel =
      (detailsRoot instanceof HTMLElement &&
        detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel], [data-quick-view-details-panel]')) ||
      null;

    const finish = (qty) => {
      // Keep all row UI synchronized (min weight / Coming Soon) because one row’s
      // selected serials reduce selectable options for other rows.
      if (detailsRoot instanceof HTMLElement) {
        detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((r) => {
          if (!(r instanceof HTMLElement)) return;
          syncRowMinusState(r);
          syncRowWeightActionLabel(r);
        });
      } else {
        syncRowMinusState(row);
      }
      if (panel instanceof HTMLElement) syncBulkMap(panel, row);
      if (detailsRoot instanceof HTMLElement) {
        updateAddSelectedState(detailsRoot);
        updateDetailsSummary(detailsRoot);
        if (!opts.skipNotify) notifyQtyUpdated(detailsRoot);
      }
      return qty;
    };

    if (!isRowAvailable(row) || input.disabled) {
      writeQty(input, 0, { silent: true });
      clearRowWeight(row);
      return finish(0);
    }

    let next = Number.isFinite(requestedQty) ? Math.max(0, Math.floor(requestedQty)) : 0;
    const sku = getRowSku(row);

    if (!sku) {
      const max = getRowQtyMax(row, input);
      if (max != null) next = Math.min(next, max);
      writeQty(input, next, { silent: true });
      return finish(next);
    }

    const exclude =
      detailsRoot instanceof HTMLElement
        ? collectSelectedSerialsOutsideRow(detailsRoot, row)
        : new Set();
    const selectable = getSelectableWeightEntriesForSku(sku, exclude);
    const selectableMax = getRowSelectableMax(row, input);
    if (selectableMax != null) next = Math.min(next, selectableMax);
    next = Math.min(next, selectable.length);

    if (next <= 0) {
      writeQty(input, 0, { silent: true });
      clearRowWeight(row);
      return finish(0);
    }

    const availableBySerial = new Map();
    selectable.forEach((entry) => {
      const serial = String(entry.serialNumber || '').trim();
      if (serial) availableBySerial.set(serial, entry);
    });

    /** @type {typeof selectable} */
    let kept = [];
    String(row.dataset.serialNumbersValue || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((serial) => {
        const entry = availableBySerial.get(serial);
        if (!entry) return;
        if (kept.some((k) => String(k.serialNumber || '').trim() === serial)) return;
        kept.push(entry);
      });

    if (kept.length > next) {
      kept.sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
      kept = kept.slice(0, next);
    }

    if (kept.length < next) {
      const keptSerials = new Set(kept.map((k) => String(k.serialNumber || '').trim()).filter(Boolean));
      const remaining = selectable.filter((entry) => {
        const serial = String(entry.serialNumber || '').trim();
        return serial && !keptSerials.has(serial);
      });
      for (let i = 0; i < remaining.length && kept.length < next; i += 1) {
        kept.push(remaining[i]);
      }
    }

    next = kept.length;
    writeQty(input, next, { silent: true });

    if (next <= 0) {
      clearRowWeight(row);
      return finish(0);
    }

    setRowWeightValue(
      row,
      kept.map((entry) => entry.label).join(', '),
      kept.map((entry) => String(entry.serialNumber || '').trim()).join(', ')
    );
    return finish(next);
  }

  /** @param {HTMLInputElement} input @param {number} val @param {{ silent?: boolean }} [opts] */
  function writeQty(input, val, opts = {}) {
    let next = Math.max(0, Number.isFinite(val) ? Math.floor(val) : 0);
    const row = input.closest('[data-details-variant-row]');
    if (row instanceof HTMLElement) {
      const max = getRowQtyMax(row, input);
      if (max != null) next = Math.min(next, max);
    }
    const asString = String(next);
    if (input.value === asString) return;
    input.value = asString;
    if (!opts.silent) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /** @param {string} weightStr */
  function parseWeightGrams(weightStr) {
    if (!weightStr) return 0;
    return weightStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((sum, part) => {
        const match = part.match(/([\d.]+)/);
        if (!match) return sum;
        const n = parseFloat(match[1]);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0);
  }

  /** @param {number} grams */
  function formatWeightTotal(grams) {
    if (!Number.isFinite(grams) || grams <= 0) return '0.00g';
    return grams.toFixed(2) + 'g';
  }

  /** @param {string} label */
  function formatWeightLabelForDisplay(label) {
    const text = String(label || '').trim();
    if (!text) return text;
    const match = text.match(/(-?\d+(?:\.\d+)?)(?:\s*([a-zA-Z]+))?/);
    if (!match) return text;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return text;
    const rawUnit = (match[2] || 'g').trim();
    const unit = rawUnit.toLowerCase();
    const grams = unit === 'kg' ? value * 1000 : value;
    if (!Number.isFinite(grams)) return text;
    return `${grams.toFixed(2)}g`;
  }

  /** @param {number} cents */
  function formatMoney(cents) {
    if (!Number.isFinite(cents) || cents <= 0) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /** @param {number} grams */
  function formatGramValue(grams) {
    if (!Number.isFinite(grams) || grams <= 0) return '—';
    return grams.toFixed(2) + 'g';
  }

  /** @param {string[]} values */
  function sortMatrixValues(values) {
    const list = values.slice();
    list.sort((a, b) => {
      const aNum = parseFloat(String(a).replace(/[^0-9.]+/g, ''));
      const bNum = parseFloat(String(b).replace(/[^0-9.]+/g, ''));
      const aHasNum = Number.isFinite(aNum);
      const bHasNum = Number.isFinite(bNum);
      if (aHasNum && bHasNum && aNum !== bNum) return aNum - bNum;
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }

  /** @param {HTMLElement} detailsRoot */
  function parseDetailsProductJson(detailsRoot) {
    const script = detailsRoot.querySelector('[data-plp-product-json]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /** @param {unknown} source */
  function getImageSrc(source) {
    if (!source) return '';
    if (typeof source === 'string') return source.trim();
    if (typeof source !== 'object') return '';
    const obj = /** @type {Record<string, unknown>} */ (source);
    const direct =
      normalizeString(obj.src) ||
      normalizeString(obj.url) ||
      normalizeString(obj.image) ||
      normalizeString(obj.featured_image);
    if (direct) return direct;
    if (obj.preview_image && typeof obj.preview_image === 'object') {
      const preview = /** @type {Record<string, unknown>} */ (obj.preview_image);
      return normalizeString(preview.src) || normalizeString(preview.url);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   * @param {number} variantId
   */
  function getVariantImageSrc(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !Number.isFinite(variantId)) return '';
    const variant =
      /** @type {Record<string, unknown> | undefined} */ (
        product.variants.find((entry) => Number(entry && entry.id) === variantId)
      ) || null;
    if (!variant) return '';

    const direct =
      getImageSrc(variant.featured_image) ||
      getImageSrc(variant.featured_media) ||
      getImageSrc(variant.image) ||
      getImageSrc(variant.preview_image);
    if (direct) return direct;

    const variantImageId = Number(variant.featured_image_id || variant.image_id);
    if (Number.isFinite(variantImageId) && Array.isArray(product.media)) {
      const mediaMatch = /** @type {Record<string, unknown> | undefined} */ (
        product.media.find((m) => Number(m && m.id) === variantImageId)
      );
      const mediaSrc = getImageSrc(mediaMatch);
      if (mediaSrc) return mediaSrc;
    }

    return '';
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   */
  function getProductFallbackImageSrc(product) {
    if (!product || typeof product !== 'object') return '';
    const direct =
      getImageSrc(product.featured_image) || getImageSrc(product.image) || getImageSrc(product.preview_image);
    if (direct) return direct;
    if (Array.isArray(product.media) && product.media.length) {
      const mediaSrc = getImageSrc(product.media[0]);
      if (mediaSrc) return mediaSrc;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const imageSrc = getImageSrc(product.images[0]);
      if (imageSrc) return imageSrc;
    }
    return '';
  }

  /** @param {unknown} value */
  function normalizeString(value) {
    return value == null ? '' : String(value).trim();
  }

  /** @param {unknown} value */
  function normalizeKey(value) {
    return normalizeString(value).toLowerCase();
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {number} idx
   * @returns {string}
   */
  function getOptionName(product, idx) {
    const opts = Array.isArray(product.options) ? product.options : [];
    const opt = opts[idx];
    if (typeof opt === 'string') return opt;
    if (opt && typeof opt === 'object' && 'name' in opt) {
      return normalizeString(opt.name);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {number} idx
   * @returns {string}
   */
  function getVariantOptionValue(variant, idx) {
    const direct = variant['option' + String(idx + 1)];
    const directStr = normalizeString(direct);
    if (directStr) return directStr;
    const arr = Array.isArray(variant.options) ? variant.options : [];
    return normalizeString(arr[idx]);
  }

  /**
   * Keep first mapped variant stable.
   * Do not prefer Shopify variant.available — WMS owns cell availability by SKU.
   * @param {Record<string, unknown> | undefined} current
   * @param {Record<string, unknown>} next
   * @returns {Record<string, unknown>}
   */
  function chooseMatrixVariant(current, next) {
    if (!current) return next;
    return current;
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {{ widthIdx: number, lengthIdx: number }}
   */
  function resolveChainAxes(product) {
    const maxOptions = Math.min(3, Array.isArray(product.options) ? product.options.length : 0);
    let widthIdx = -1;
    let lengthIdx = -1;

    for (let i = 0; i < maxOptions; i += 1) {
      const key = normalizeKey(getOptionName(product, i));
      if (widthIdx < 0 && /(width|mm|thickness|gauge|dia|diameter)/.test(key)) widthIdx = i;
      if (lengthIdx < 0 && /(length|inch|inches|len)/.test(key)) lengthIdx = i;
    }

    if (widthIdx < 0 && maxOptions > 0) widthIdx = 0;
    if (lengthIdx < 0) {
      if (maxOptions > 1) lengthIdx = widthIdx === 0 ? 1 : 0;
      else lengthIdx = widthIdx;
    }
    if (lengthIdx === widthIdx && maxOptions > 1) {
      lengthIdx = widthIdx === 0 ? 1 : 0;
    }
    return { widthIdx, lengthIdx };
  }

  /**
   * @param {Record<string, unknown>} variant
   * @returns {number}
   */
  function getVariantWeightGrams(variant) {
    const raw = Number(variant.weight);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    const unit = normalizeKey(variant.weight_unit);
    if (unit === 'kg') return raw * 1000;
    return raw;
  }

  /**
   * @param {Record<string, Record<string, Record<string, unknown>>>} map
   * @param {string[]} widths
   * @param {string[]} lengths
   */
  function countMatrixVariants(map, widths, lengths) {
    let count = 0;
    for (const width of widths) {
      for (const length of lengths) {
        if (map[width]?.[length]) count += 1;
      }
    }
    return count;
  }

  /** @param {number} count */
  function formatMatrixVariantCount(count) {
    const total = Math.max(0, Math.floor(Number(count) || 0));
    return `${total} ${total === 1 ? 'Variant' : 'Variants'}`;
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {'in-stock'|'low-stock'|'sold-out'|string} stockLevel
   */
  function getMatrixAvailableQuantity(variant, stockLevel) {
    if (stockLevel !== 'in-stock' && stockLevel !== 'low-stock') return null;
    const qty = Number(variant.inventory_quantity);
    if (!Number.isFinite(qty)) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[chain-matrix] missing inventory_quantity for variant', variant.id);
      }
      return null;
    }
    const normalized = Math.max(0, Math.floor(qty));
    if (normalized <= 0) return null;
    return normalized;
  }

  /**
   * @param {'in-stock'|'low-stock'|'sold-out'|string} stockLevel
   * @param {Record<string, unknown>} variant
   */
  function buildMatrixAvailabilityHtml(stockLevel, variant) {
    const availableQty = getMatrixAvailableQuantity(variant, stockLevel);
    if (availableQty === null) return '';
    const modClass =
      stockLevel === 'low-stock'
        ? 'order-sheet-details-chain-matrix__availability--low-stock'
        : 'order-sheet-details-chain-matrix__availability--in-stock';
    return `<p class="order-sheet-details-chain-matrix__availability bulk-matrix-availability ${modClass}">Available: ${availableQty}</p>`;
  }

  /**
   * @param {HTMLElement} matrixHost
   * @param {number} count
   */
  function updateMatrixVariantCountLabel(matrixHost, count) {
    const countEl = matrixHost.querySelector('[data-chain-matrix-variant-count]');
    if (!(countEl instanceof HTMLElement)) return;
    countEl.textContent = formatMatrixVariantCount(count);
    countEl.removeAttribute('aria-hidden');
  }

  /**
   * @param {HTMLElement} scrollEl
   */
  function syncChainMatrixStickyHeaderHeights(scrollEl) {
    if (!(scrollEl instanceof HTMLElement)) return;
    const groupHeader = scrollEl.querySelector(
      '.order-sheet-details-chain-matrix__length-group, .quick-view-chain-matrix__length-group'
    );
    const subHeader = scrollEl.querySelector(
      '.order-sheet-details-chain-matrix__length, .quick-view-chain-matrix__length'
    );
    if (groupHeader instanceof HTMLElement) {
      const mainHeight = Math.ceil(groupHeader.getBoundingClientRect().height);
      if (mainHeight >= 24 && mainHeight <= 80) {
        scrollEl.style.setProperty('--matrix-main-header-height', `${mainHeight}px`);
      } else {
        scrollEl.style.removeProperty('--matrix-main-header-height');
      }
    }
    if (subHeader instanceof HTMLElement) {
      const subHeight = Math.ceil(subHeader.getBoundingClientRect().height);
      if (subHeight >= 24 && subHeight <= 80) {
        scrollEl.style.setProperty('--matrix-sub-header-height', `${subHeight}px`);
      } else {
        scrollEl.style.removeProperty('--matrix-sub-header-height');
      }
    }
  }

  /**
   * @param {HTMLElement} scrollEl
   */
  function bindChainMatrixStickyHeaderSync(scrollEl) {
    if (!(scrollEl instanceof HTMLElement)) return;
    syncChainMatrixStickyHeaderHeights(scrollEl);
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @returns {boolean}
   */
  function buildChainMatrix(detailsRoot) {
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    if (!(matrixHost instanceof HTMLElement)) return false;
    if (matrixHost.dataset.matrixBuilt === 'true') return true;

    const product = parseDetailsProductJson(detailsRoot);
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return false;

    matrixHost.dataset.productType = normalizeString(product.product_type || product.type);

    const { widthIdx, lengthIdx } = resolveChainAxes(product);
    const widthsSet = new Set();
    const lengthsSet = new Set();
    /** @type {Record<string, Record<string, Record<string, unknown>>>} */
    const map = Object.create(null);

    for (const variant of product.variants) {
      if (!variant || typeof variant !== 'object') continue;
      const widthVal = getVariantOptionValue(variant, widthIdx) || '—';
      const lengthVal = getVariantOptionValue(variant, lengthIdx) || '—';
      widthsSet.add(widthVal);
      lengthsSet.add(lengthVal);
      if (!map[widthVal]) map[widthVal] = Object.create(null);
      map[widthVal][lengthVal] = chooseMatrixVariant(
        /** @type {Record<string, unknown> | undefined} */ (map[widthVal][lengthVal]),
        variant
      );
    }

    const widths = sortMatrixValues(Array.from(widthsSet));
    const lengths = sortMatrixValues(Array.from(lengthsSet));
    if (!widths.length || !lengths.length) return false;

    const head = matrixHost.querySelector('[data-chain-matrix-head]');
    const body = matrixHost.querySelector('[data-chain-matrix-body]');
    const table = matrixHost.querySelector('.order-sheet-details-chain-matrix__table');
    if (!(head instanceof HTMLElement) || !(body instanceof HTMLElement)) return false;

    if (table instanceof HTMLTableElement) {
      let colgroup = table.querySelector('colgroup');
      if (!colgroup) {
        colgroup = document.createElement('colgroup');
        table.insertBefore(colgroup, table.firstChild);
      }
      colgroup.innerHTML =
        '<col class="order-sheet-details-chain-matrix__col-axis">' +
        `<col span="${lengths.length}">`;
    }

    const lowThreshold = Number(matrixHost.dataset.lowStockThreshold || 5) || 5;
    const weightPlaceholder = matrixHost.dataset.weightPlaceholder || 'Select Weight';
    const stockAvailableLabel = matrixHost.dataset.stockAvailableLabel || 'In Stock';
    const stockSoldOutLabel = 'Out of Stock';
    const stockLowLabel = matrixHost.dataset.stockLowLabel || 'Low Stock';
    const productId = normalizeString(detailsRoot.dataset.productId);

    let headHtml =
      '<tr><th scope="col" rowspan="2" class="order-sheet-details-chain-matrix__corner order-sheet-details-chain-matrix__corner-row1">' +
      '<span class="order-sheet-details-chain-matrix__corner-label">WIDTH (MM)</span></th>' +
      `<th scope="col" colspan="${lengths.length}" class="order-sheet-details-chain-matrix__length-group">Length (inches)</th></tr><tr>`;
    for (const length of lengths) {
      headHtml += `<th scope="col" class="order-sheet-details-chain-matrix__length">${escapeHtml(length)}</th>`;
    }
    headHtml += '</tr>';
    head.innerHTML = headHtml;

    let bodyHtml = '';
    for (const width of widths) {
      bodyHtml += `<tr><th scope="row" class="order-sheet-details-chain-matrix__width">${escapeHtml(width)}</th>`;
      for (const length of lengths) {
        const variant = map[width]?.[length];
        if (!variant) {
          bodyHtml += `
            <td class="order-sheet-details-chain-matrix__cell order-sheet-details-chain-matrix__cell--missing">
              <div class="order-sheet-details-chain-matrix__variant order-sheet-details-chain-matrix__variant--disabled">
                <p class="order-sheet-details-chain-matrix__stock-badge-wrap">
                  <span class="order-sheet-details-chain-matrix__stock-badge order-sheet-details-chain-matrix__stock-badge--missing">Variant Not Available</span>
                </p>
                <div class="order-sheet-details-qty quantity-control" role="group" aria-label="Variant not available">
                  <button type="button" class="order-sheet-details-qty__btn" disabled aria-label="Decrease quantity">−</button>
                  <input type="number" class="order-sheet-details-qty__input" value="0" min="0" step="1" disabled aria-label="Quantity">
                  <button type="button" class="order-sheet-details-qty__btn" disabled aria-label="Increase quantity">+</button>
                </div>
                <div class="order-sheet-details-chain-matrix__weight-wrap">
                  <button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-chain-matrix__weight-btn" disabled>
                    <span class="order-sheet-details-chain-matrix__weight-na">Variant Not Available</span>
                  </button>
                </div>
              </div>
            </td>
          `;
          continue;
        }

        const variantId = Number(variant.id);
        const available = false;
        const sku = normalizeString(variant.sku);
        const priceCents = Number(variant.price) || 0;
        const stockLevel = 'loading';
        const stockText = 'CHECKING AVAILABILITY';
        const disabledAttr = ' disabled';
        const rowClass = ' order-sheet-details-chain-matrix__variant--sold-out';
        const qtyId = `PlpChainQty-${productId}-${variantId}`;
        const availabilityHtml = '';
        const invQty = 0;
        const maxAttr = ' max="0" data-variant-inventory-qty="0"';

        bodyHtml += `
          <td class="order-sheet-details-chain-matrix__cell order-sheet-details-chain-matrix__cell--${stockLevel}">
            <div
              class="order-sheet-details-chain-matrix__variant${rowClass}"
              data-details-variant-row
              data-variant-id="${variantId}"
              data-variant-sku="${escapeHtml(sku)}"
              data-variant-price-cents="${priceCents}"
              data-variant-available="${available ? 'true' : 'false'}"
              data-wms-status="loading"
              data-matrix-width="${escapeHtml(width)}"
              data-matrix-length="${escapeHtml(length)}" data-variant-inventory-qty="${invQty}"
            >
              <p class="order-sheet-details-chain-matrix__stock-badge-wrap">
                <span class="order-sheet-details-chain-matrix__stock-badge order-sheet-details-chain-matrix__stock-badge--${stockLevel}">
                  ${escapeHtml(stockText)}
                </span>
              </p>
              ${availabilityHtml}
              <div class="order-sheet-details-qty quantity-control" role="group" aria-label="Quantity for ${escapeHtml(width)} x ${escapeHtml(length)}">
                <button type="button" class="order-sheet-details-qty__btn" data-details-qty-minus aria-label="Decrease quantity"${disabledAttr}>−</button>
                <input
                  type="number"
                  id="${escapeHtml(qtyId)}"
                  class="order-sheet-details-qty__input"
                  value="0"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  data-details-qty-input
                  aria-label="Quantity for ${escapeHtml(width)} x ${escapeHtml(length)}"
                  ${disabledAttr}${maxAttr}
                >
                <button type="button" class="order-sheet-details-qty__btn" data-details-qty-plus aria-label="Increase quantity"${disabledAttr}>+</button>
              </div>
              <div class="order-sheet-details-chain-matrix__weight-wrap">
                ${
                  sku
                    ? `<button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-chain-matrix__weight-btn" data-details-weight-open aria-haspopup="dialog" data-matrix-width="${escapeHtml(width)}" data-matrix-length="${escapeHtml(length)}"${disabledAttr}><span data-details-weight-label>${escapeHtml(weightPlaceholder)}</span></button>`
                    : `<button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-chain-matrix__weight-btn" disabled><span class="order-sheet-details-chain-matrix__weight-na">Variant Not Available</span></button>`
                }
              </div>
            </div>
          </td>
        `;
      }
      bodyHtml += '</tr>';
    }

    body.innerHTML = bodyHtml;
    updateMatrixVariantCountLabel(matrixHost, countMatrixVariants(map, widths, lengths));
    matrixHost.dataset.matrixBuilt = 'true';
    const scrollEl = matrixHost.querySelector('.order-sheet-details-chain-matrix__scroll');
    if (scrollEl instanceof HTMLElement) {
      requestAnimationFrame(() => bindChainMatrixStickyHeaderSync(scrollEl));
    }
    return true;
  }

  /** @param {HTMLElement} detailsRoot */
  function notifyQtyUpdated(detailsRoot) {
    detailsRoot.dispatchEvent(new CustomEvent('plp-details-qty-update', { bubbles: true }));
  }

  /** @param {HTMLElement} row */
  function isRowAvailable(row) {
    return row.dataset.variantAvailable === 'true';
  }

  /** @param {HTMLElement} row */
  function getRowQtyInput(row) {
    return /** @type {HTMLInputElement | null} */ (row.querySelector('[data-details-qty-input]'));
  }

  /** @param {HTMLElement} row */
  function getRowSku(row) {
    return String(row.dataset.variantSku || '').trim();
  }

  /** @returns {any} */
  function getWmsAvailabilityApi() {
    return (
      /** @type {any} */ (window).WMSInventory ||
      /** @type {any} */ (window).AOneWmsAvailability ||
      null
    );
  }

  /**
   * @param {HTMLElement} row
   * @param {'loading'|'available'|'coming-soon'|'all-in-cart'|'error'} status
   * @param {number} count
   */
  function setRowWmsStatus(row, status, count) {
    const safeCount = Number.isFinite(Number(count)) ? Math.max(0, Math.floor(Number(count))) : 0;
    row.dataset.wmsStatus = status;
    row.dataset.variantInventoryQty = String(safeCount);
    row.dataset.variantAvailable = status === 'available' ? 'true' : 'false';
    row.dataset.cellState = status;
    row.classList.toggle('is-unavailable', status !== 'available');

    const availabilityApi = getWmsAvailabilityApi();
    const stockBadge = row.querySelector(
      '.order-sheet-details-variant-table__stock-badge, .order-sheet-details-chain-matrix__stock-badge'
    );
    const matrixStatus = row.querySelector('.premium-pdp-matrix__status');
    const qvStockQty = row.querySelector('.quick-view-stock-qty');
    const qvMatrixStatusText = row.querySelector('.quick-view-chain-matrix__status-text');
    const statusLabel =
      status === 'loading'
        ? null
        : status === 'available'
          ? `AVAILABLE QTY: ${safeCount}`
          : status === 'all-in-cart'
            ? 'AVAILABLE QTY: 0'
            : status === 'coming-soon'
              ? 'COMING SOON'
              : 'UNAVAILABLE';

    if (qvStockQty instanceof HTMLElement && status !== 'loading') {
      qvStockQty.textContent =
        status === 'available' ? String(safeCount) : status === 'all-in-cart' ? '0' : '—';
    }
    if (qvMatrixStatusText instanceof HTMLElement && statusLabel) {
      qvMatrixStatusText.textContent = statusLabel;
    }

    if (stockBadge instanceof HTMLElement) {
      stockBadge.classList.remove(
        'order-sheet-details-variant-table__stock-badge--available',
        'order-sheet-details-variant-table__stock-badge--sold-out',
        'order-sheet-details-chain-matrix__stock-badge--in-stock',
        'order-sheet-details-chain-matrix__stock-badge--low-stock',
        'order-sheet-details-chain-matrix__stock-badge--sold-out'
      );
      if (status === 'loading') {
        stockBadge.innerHTML =
          availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
            ? availabilityApi.getInventorySkeletonMarkup('status')
            : '<span class="aone-inventory-skeleton aone-inventory-skeleton--status" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
        stockBadge.setAttribute('aria-busy', 'true');
      } else if (status === 'available') {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--available');
      } else if (status === 'all-in-cart') {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--sold-out');
      } else if (status === 'coming-soon') {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--sold-out');
      } else {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--sold-out');
      }
    }

    if (matrixStatus instanceof HTMLElement) {
      matrixStatus.className = `premium-pdp-matrix__status premium-pdp-matrix__status--${status}`;
      if (status === 'loading') {
        matrixStatus.innerHTML =
          availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
            ? availabilityApi.getInventorySkeletonMarkup('status')
            : '<span class="aone-inventory-skeleton aone-inventory-skeleton--status" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
        matrixStatus.setAttribute('aria-busy', 'true');
      } else {
        matrixStatus.removeAttribute('aria-busy');
        const compact =
          status === 'available'
            ? `QTY: ${safeCount}`
            : status === 'all-in-cart'
              ? 'QTY: 0'
              : statusLabel;
        const sku = String(row.dataset.variantSku || '').trim();
        matrixStatus.innerHTML =
          `<span class="premium-pdp-matrix__status-row">` +
          `<span class="premium-pdp-matrix__status-label premium-pdp-matrix__status-label--desktop">${statusLabel}</span>` +
          `<span class="premium-pdp-matrix__status-label premium-pdp-matrix__status-label--compact" aria-hidden="true">${compact}</span>` +
          `<button type="button" class="premium-pdp-matrix__tip-btn" aria-label="View SKU: ${sku || 'Unavailable'}" title="SKU: ${sku || 'Unavailable'}"${sku ? '' : ' disabled'}>` +
          `<svg class="premium-pdp-matrix__tip-icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"></circle><circle cx="8" cy="5.25" r="0.9" fill="currentColor"></circle><path d="M8 7.35v3.9" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"></path></svg>` +
          `</button></span>`;
      }
    }
  }

  /** @param {HTMLElement} detailsRoot */
  async function hydrateDetailsWmsAvailability(detailsRoot) {
    const availabilityApi = getWmsAvailabilityApi();
    const productId = normalizeString(detailsRoot.dataset.productId);
    if (!availabilityApi || !productId) return;

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      setRowWmsStatus(row, 'loading', 0);
      syncRowWeightActionLabel(row, { isLoading: true });
      syncRowMinusState(row);
      syncWeightButtonState(row);
    });
    updateAddSelectedState(detailsRoot);

    await availabilityApi.ensureProductsLoaded([productId]);
    try {
      const forceCart =
        typeof availabilityApi.isCartStateReady === 'function'
          ? !availabilityApi.isCartStateReady()
          : true;
      await availabilityApi.ensureCartSerialsSynced?.(forceCart);
    } catch (_error) {
      /* continue — unresolved cart keeps rows in loading via getSkuAvailability */
    }
    const productState = availabilityApi.getProductAvailability?.(productId);
    if (!productState || productState.status === 'loading' || productState.status === 'idle' || productState.loading) {
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        setRowWmsStatus(row, 'loading', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
        syncWeightButtonState(row);
      });
      updateAddSelectedState(detailsRoot);
      updateDetailsSummary(detailsRoot);
      return;
    }
    if (productState.status !== 'loaded') {
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        setRowWmsStatus(row, 'error', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
      });
      updateAddSelectedState(detailsRoot);
      updateDetailsSummary(detailsRoot);
      return;
    }

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const sku = getRowSku(row);
      if (!sku) {
        setRowWmsStatus(row, 'coming-soon', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
        return;
      }
      const skuState = availabilityApi.getSkuAvailability?.(productId, sku);
      if (!skuState || skuState.status === 'loading' || skuState.status === 'idle') {
        setRowWmsStatus(row, 'loading', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
      } else if (skuState.status !== 'loaded') {
        setRowWmsStatus(row, 'error', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
      } else {
        const rawCount = Math.max(0, Math.floor(Number(skuState.rawAvailableCount ?? skuState.count) || 0));
        const effectiveCount = Math.max(0, Math.floor(Number(skuState.count) || 0));
        const storefrontStatus =
          skuState.storefrontStatus ||
          (availabilityApi.getStorefrontStatus
            ? availabilityApi.getStorefrontStatus(rawCount, effectiveCount)
            : effectiveCount > 0
              ? 'AVAILABLE'
              : rawCount > 0
                ? 'ALL_IN_CART'
                : 'COMING_SOON');

        if (skuState.found === true && storefrontStatus === 'AVAILABLE' && effectiveCount > 0) {
          setRowWmsStatus(row, 'available', effectiveCount);
          if (skuState.minWeight != null && Number(skuState.minWeight) > 0) {
            row.dataset.wmsMinWeight = String(skuState.minWeight);
          } else {
            delete row.dataset.wmsMinWeight;
          }
          if (Array.isArray(skuState.availableOptions) && skuState.availableOptions.length) {
            try {
              row.dataset.wmsOptionsJson = JSON.stringify(skuState.availableOptions);
            } catch (_error) {
              delete row.dataset.wmsOptionsJson;
            }
            const shared = getSharedWeightStore();
            const rawOptions = Array.isArray(skuState.rawAvailableOptions)
              ? skuState.rawAvailableOptions
              : skuState.availableOptions;
            if (shared?.rawBySku && Array.isArray(rawOptions) && rawOptions.length) {
              shared.rawBySku[sku.toLowerCase()] = {
                availableOptions: rawOptions,
                sku,
              };
            }
            const seeded = extractWeightEntries(rawOptions, { skipReservedFilter: true });
            if (seeded.length) setWeightCacheForSku(sku, { entries: seeded });
          } else {
            delete row.dataset.wmsOptionsJson;
          }
          const input = getRowQtyInput(row);
          if (input) {
            input.max = String(effectiveCount);
            input.disabled = false;
            input.removeAttribute('aria-disabled');
            const current = readQty(input);
            if (current > effectiveCount) writeQty(input, effectiveCount, { silent: true });
          }
          const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
          if (plus) {
            plus.disabled = false;
            plus.removeAttribute('aria-disabled');
          }
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) {
            weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
            weightBtn.removeAttribute('aria-disabled');
          }
          syncRowWeightActionLabel(row);
        } else if (skuState.found === true && storefrontStatus === 'ALL_IN_CART') {
          setRowWmsStatus(row, 'all-in-cart', 0);
          const input = getRowQtyInput(row);
          if (input) writeQty(input, 0, { silent: true });
          clearRowWeight(row);
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
          syncRowWeightActionLabel(row);
        } else {
          setRowWmsStatus(row, 'coming-soon', 0);
          const input = getRowQtyInput(row);
          if (input) writeQty(input, 0, { silent: true });
          clearRowWeight(row);
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
          syncRowWeightActionLabel(row);
        }
      }
      syncRowMinusState(row);
      syncWeightButtonState(row);
    });

    updateAddSelectedState(detailsRoot);
    updateDetailsSummary(detailsRoot);
  }

  /** @param {HTMLElement} row */
  function getRowQty(row) {
    const input = getRowQtyInput(row);
    return input ? readQty(input) : 0;
  }

  /** @param {HTMLElement} row */
  function getWeightModalForRow(row) {
    const details = row.closest('[data-plp-expanded-details]');
    if (!(details instanceof HTMLElement)) return null;
    return getWeightModalState(details)?.modal ?? null;
  }

  /** @param {HTMLElement} row */
  function getWeightPlaceholderForRow(row) {
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    const idleLabel = btn?.getAttribute('data-weight-idle-label');
    // If we already computed a real idle label (e.g. "Min wt: 8.94g"), prefer it.
    if (idleLabel && idleLabel !== 'Select Weight' && idleLabel !== 'Edit Weight') return idleLabel;
    const availabilityApi = getWmsAvailabilityApi();
    if (availabilityApi && typeof availabilityApi.resolveWeightActionLabel === 'function') {
      const label = availabilityApi.resolveWeightActionLabel({
        quantity: 0,
        selectedSerialNumbers: '',
        isLoading: false,
        isAvailable: isRowAvailable(row),
      });
      if (label) return label;
    }
    const modal = getWeightModalForRow(row);
    return modal?.dataset.weightPlaceholder || 'Select Weight';
  }

  /**
   * Apply shared Select Weight / Edit Weight label (or skeleton while loading).
   * @param {HTMLElement} row
   * @param {{ isLoading?: boolean }} [options]
   */
  function syncRowWeightActionLabel(row, options = {}) {
    const labelEl = row.querySelector('[data-details-weight-label]');
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (!(labelEl instanceof HTMLElement)) return;

    const availabilityApi = getWmsAvailabilityApi();
    const selected = String(row.dataset.serialNumbersValue || '').trim();
    const isLoading =
      options.isLoading === true ||
      row.dataset.wmsStatus === 'loading' ||
      row.dataset.qvmInventoryPending === 'true';
    const isAvailable = isRowAvailable(row);

    if (isLoading) {
      labelEl.innerHTML =
        availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
          ? availabilityApi.getInventorySkeletonMarkup('weight-action')
          : '<span class="aone-inventory-skeleton aone-inventory-skeleton--weight-action" aria-hidden="true"></span>';
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('data-weight-idle-label', '');
      }
      return;
    }

    const sku = getRowSku(row);
    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]') ||
      row.closest('[data-order-sheet-details]') ||
      null;
    const exclude =
      detailsRoot instanceof HTMLElement ? collectSelectedSerialsOutsideRow(detailsRoot, row) : new Set();

    const selectable = sku ? getSelectableWeightEntriesForSku(sku, exclude) : [];
    const effectiveQty = selectable.length;
    const minEntry = selectable.length ? selectable[0] : null;
    const minWeight = minEntry ? parseEntryWeightGrams(minEntry) : null;
    const unit = minEntry && minEntry.unit ? String(minEntry.unit) : 'g';

    const state =
      availabilityApi && typeof availabilityApi.resolveWeightButtonState === 'function'
        ? availabilityApi.resolveWeightButtonState({
            isLoading: false,
            isAvailable,
            effectiveAvailableQuantity: effectiveQty,
            minimumWeight: minWeight,
            minimumWeightLabel: effectiveQty > 0 ? availabilityApi.formatMinimumWeightLabel(minWeight, { unit }) : null,
            selectedSerialNumbers: selected,
            unit,
          })
        : {
            state: 'minimum-weight',
            label:
              !isAvailable || effectiveQty <= 0
                ? 'Coming Soon'
                : selected
                  ? 'Edit weight'
                  : minWeight != null && Number.isFinite(Number(minWeight)) && Number(minWeight) > 0
                    ? `Min wt: ${Number(minWeight).toFixed(3).replace(/\.?0+$/, '')}${unit}`
                    : 'Coming Soon',
            disabled: !isAvailable || effectiveQty <= 0,
          };

    if (btn) {
      btn.removeAttribute('aria-busy');
      btn.setAttribute('data-weight-idle-label', state.label || 'Min wt: N/A');
      btn.setAttribute('aria-disabled', state.disabled ? 'true' : 'false');
      // Keep syncWeightButtonState as the source of truth for disabled, but align early for accessibility.
      if (btn instanceof HTMLButtonElement) btn.disabled = Boolean(state.disabled);
    }
    labelEl.textContent = state.label || 'Coming Soon';
  }

  /** @param {HTMLElement} row */
  function clearRowWeight(row) {
    delete row.dataset.weightValue;
    delete row.dataset.serialNumbersValue;
    row.classList.remove('has-weight-selected');
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (btn) btn.classList.remove('is-confirmed');
    syncRowWeightActionLabel(row);
  }

  /** @param {HTMLElement} row */
  function syncWeightButtonState(row) {
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (!btn) return;
    const sku = getRowSku(row);
    const available = isRowAvailable(row);
    const loading = row.dataset.wmsStatus === 'loading' || row.dataset.qvmInventoryPending === 'true';

    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]') ||
      row.closest('[data-order-sheet-details]') ||
      null;
    const exclude =
      detailsRoot instanceof HTMLElement ? collectSelectedSerialsOutsideRow(detailsRoot, row) : new Set();
    const selectable = sku ? getSelectableWeightEntriesForSku(sku, exclude) : [];
    const effectiveQty = selectable.length;

    const disabled = loading || !available || !sku || effectiveQty <= 0;
    btn.disabled = disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  /** @param {HTMLElement} row @param {string} value @param {string} [serialValue] */
  function setRowWeightValue(row, value, serialValue) {
    const trimmed = String(value || '').trim();
    const serialTrimmed = String(serialValue || '').trim();
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (!trimmed) {
      clearRowWeight(row);
      return;
    }

    row.dataset.weightValue = trimmed;
    if (serialTrimmed) row.dataset.serialNumbersValue = serialTrimmed;
    else delete row.dataset.serialNumbersValue;
    row.classList.add('has-weight-selected');
    if (btn) btn.classList.add('is-confirmed');
    syncRowWeightActionLabel(row);
    syncWeightButtonState(row);
  }

  /** @param {HTMLElement} row */
  function isRowQtyModalOnly(row) {
    return !!row.querySelector('[data-details-qty-modal-only]');
  }

  /** @param {HTMLElement} row */
  function syncRowMinusState(row) {
    const input = getRowQtyInput(row);
    const displayInput = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-details-qty-display]'));
    const minus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-minus]'));
    const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
    if (!input || !minus) return;
    if (displayInput) displayInput.value = input.value;

    if (isRowQtyModalOnly(row)) {
      minus.disabled = true;
      if (plus) plus.disabled = true;
      input.readOnly = true;
      syncWeightButtonState(row);
      return;
    }

    const sku = getRowSku(row);
    const qty = readQty(input);
    const max = getRowSelectableMax(row, input);

    const effectiveMax = max != null && Number.isFinite(Number(max)) ? Number(max) : 0;
    const available = isRowAvailable(row) && Boolean(sku);
    input.disabled = !available || effectiveMax <= 0;
    input.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');

    minus.disabled = !available || qty <= 0;
    if (plus) {
      plus.disabled = !available || (effectiveMax <= 0 || qty >= effectiveMax);
    }
    syncWeightButtonState(row);
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} row
   */
  function syncBulkMap(panel, row) {
    const host = panel.closest('.plp-view-host');
    const bulk = window.plpBulkSelection;
    if (!(host instanceof HTMLElement) || !bulk) return;
    const variantId = Number(row.dataset.variantId);
    if (!Number.isFinite(variantId)) return;
    const input = getRowQtyInput(row);
    const qty = input ? readQty(input) : 0;
    const sku = getRowSku(row);
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    bulk.setQty(host, variantId, qty, { sku, weight, serialNumbers });
  }

  /** @param {HTMLElement} detailsRoot */
  function resetVariantTable(detailsRoot) {
    const panel = detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    const host = panel?.closest('.plp-view-host');
    if (host instanceof HTMLElement && window.plpBulkSelection) {
      window.plpBulkSelection.clearProductVariants(host, detailsRoot);
    }

    closeWeightModal(detailsRoot);

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const input = getRowQtyInput(row);
      if (input) {
        writeQty(input, 0, { silent: true });
        input.disabled = !isRowAvailable(row);
      }
      const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
      const minus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-minus]'));
      if (plus) plus.disabled = !isRowAvailable(row);
      if (minus) minus.disabled = true;
      clearRowWeight(row);
      syncWeightButtonState(row);
    });
    updateAddSelectedState(detailsRoot);
    updateDetailsSummary(detailsRoot);
    notifyQtyUpdated(detailsRoot);
  }

  /** @param {HTMLElement} detailsRoot */
  function hasSelectedQty(detailsRoot) {
    return Array.from(detailsRoot.querySelectorAll('[data-details-variant-row]')).some((row) => {
      if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return false;
      const input = getRowQtyInput(row);
      return input ? readQty(input) > 0 : false;
    });
  }

  /** @param {HTMLElement} detailsRoot */
  function updateDetailsSummary(detailsRoot) {
    let selectedVariants = 0;
    let totalQty = 0;
    let totalWeight = 0;
    let totalPriceCents = 0;

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return;
      const qty = getRowQty(row);
      if (qty <= 0) return;
      selectedVariants += 1;
      totalQty += qty;
      totalWeight += parseWeightGrams(String(row.dataset.weightValue || ''));
      const priceCents = Number(row.dataset.variantPriceCents || 0);
      if (Number.isFinite(priceCents) && priceCents > 0) {
        totalPriceCents += priceCents * qty;
      }
    });

    const roundedWeight = formatWeightTotal(totalWeight);
    detailsRoot.querySelectorAll('[data-details-selected-count]').forEach((el) => {
      el.textContent = String(selectedVariants);
    });
    detailsRoot.querySelectorAll('[data-details-total-qty], [data-summary-total-qty]').forEach((el) => {
      el.textContent = String(totalQty);
    });
    detailsRoot.querySelectorAll('[data-details-total-weight], [data-summary-total-weight]').forEach((el) => {
      el.textContent = roundedWeight;
    });
    detailsRoot.querySelectorAll('[data-details-estimated-price]').forEach((el) => {
      el.textContent = formatMoney(totalPriceCents);
    });

    const hasMeaningfulTotals = totalQty > 0 || totalWeight > 0;
    const panel = detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    const row = panel?.closest(ITEM_SEL)?.querySelector(ROW_SEL);
    if (row instanceof HTMLElement) {
      row.querySelectorAll('[data-row-total-qty]').forEach((el) => {
        el.textContent = String(totalQty);
      });
      row.querySelectorAll('[data-row-total-weight]').forEach((el) => {
        el.textContent = roundedWeight;
      });
      row.classList.toggle('has-order-summary', hasMeaningfulTotals);
    }
  }

  /** @param {HTMLElement} detailsRoot */
  function updateAddSelectedState(detailsRoot) {
    const btn = /** @type {HTMLButtonElement | null} */ (detailsRoot.querySelector('[data-details-add-selected]'));
    if (!btn || btn.dataset.addState === 'loading') return;
    btn.disabled = !hasSelectedQty(detailsRoot);
  }

  /** @param {HTMLButtonElement} btn @param {'idle'|'loading'|'success'} state */
  function setAddSelectedButtonState(btn, state) {
    const textEl = btn.querySelector('[data-details-add-selected-text]');
    const label = btn.dataset.addLabel || 'ADD SELECTED TO CART';
    const loading = btn.dataset.addLoading || 'ADDING…';
    const success = btn.dataset.addSuccess || 'ADDED';

    btn.dataset.addState = state;
    if (state === 'loading') {
      btn.disabled = true;
      if (textEl) textEl.textContent = loading;
      return;
    }
    if (state === 'success') {
      btn.disabled = true;
      if (textEl) textEl.textContent = success;
      window.setTimeout(() => {
        btn.dataset.addState = 'idle';
        if (textEl) textEl.textContent = label;
        const details = btn.closest('[data-plp-expanded-details]');
        if (details instanceof HTMLElement) updateAddSelectedState(details);
      }, 1800);
      return;
    }
    btn.dataset.addState = 'idle';
    if (textEl) textEl.textContent = label;
  }

  /** @param {HTMLElement} detailsRoot */
  function collectCartItems(detailsRoot) {
    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }[]} */
    const items = [];
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return;
      const input = getRowQtyInput(row);
      const qty = input ? readQty(input) : 0;
      if (qty <= 0) return;
      const id = Number(row.dataset.variantId);
      if (!Number.isFinite(id) || id <= 0) return;

      const sku = getRowSku(row);
      const weight = String(row.dataset.weightValue || '').trim();
      const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
      /** @type {Record<string, string>} */
      const properties = {};
      if (sku) properties.SKU = sku;
      if (weight) properties.Weight = weight;
      if (serialNumbers) properties.SerialNumbers = serialNumbers;

      const goldPricing = /** @type {typeof window.AoneGoldPricing | undefined} */ (window.AoneGoldPricing);
      if (goldPricing) {
        const material = goldPricing.resolveJewelryMaterial({ row });
        Object.assign(properties, goldPricing.attachCaptureProperties(properties, material));
      }

      const item = /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */ ({
        id,
        quantity: qty,
      });
      if (Object.keys(properties).length) item.properties = properties;
      items.push(item);
    });
    return items;
  }

  /** @param {HTMLElement} detailsRoot */
  function validateWeightSelections(detailsRoot) {
    /** @type {string[]} */
    const errors = [];
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return;
      const qty = getRowQty(row);
      if (qty <= 0) return;
      const sku = getRowSku(row);
      if (!sku) return;
      const weight = String(row.dataset.weightValue || '').trim();
      const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
      if (!weight || !serialNumbers) {
        errors.push('Select weight for all variants with quantity greater than 0.');
        return;
      }
      const count = weight.split(',').map((s) => s.trim()).filter(Boolean).length;
      const serialCount = serialNumbers.split(',').map((s) => s.trim()).filter(Boolean).length;
      if (count !== qty || serialCount !== qty) {
        errors.push('Weight selection must match quantity for each variant.');
      }
    });
    return errors.length ? errors[0] : '';
  }

  /** @param {HTMLElement} panel @param {HTMLElement} detailsRoot */
  async function addSelectedToCart(panel, detailsRoot) {
    const host = panel.closest('.plp-view-host');
    const addUrl = host instanceof HTMLElement ? host.dataset.cartAddJs : '';
    const cartJsonUrl = host instanceof HTMLElement ? host.dataset.cartJson : '';
    const btn = /** @type {HTMLButtonElement | null} */ (detailsRoot.querySelector('[data-details-add-selected]'));
    if (!btn || !addUrl) return;

    const weightError = validateWeightSelections(detailsRoot);
    if (weightError) {
      showToast(weightError, 'error');
      return;
    }

    const items = collectCartItems(detailsRoot);
    if (!items.length) {
      showToast('Select at least one available variant with quantity greater than 0.', 'error');
      return;
    }

    const availabilityApi = getWmsAvailabilityApi();
    if (availabilityApi?.guardItems) {
      const guardItemsPayload = items.map((item) => {
        const row = detailsRoot.querySelector(`[data-details-variant-row][data-variant-id="${item.id}"]`);
        const sku = row instanceof HTMLElement ? getRowSku(row) : '';
        return {
          id: item.id,
          quantity: item.quantity,
          sku,
          productId: detailsRoot.dataset.productId,
          properties: item.properties,
        };
      });
      const guard = await availabilityApi.guardItems(guardItemsPayload);
      if (!guard?.ok) {
        showToast(guard.message || 'AVAILABILITY UNAVAILABLE', 'error');
        updateAddSelectedState(detailsRoot);
        return;
      }
    }

    const goldPricing = /** @type {typeof window.AoneGoldPricing | undefined} */ (window.AoneGoldPricing);
    if (goldPricing) {
      await goldPricing.waitForReady();
    }

    setAddSelectedButtonState(btn, 'loading');
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.status || body.message) {
        const msg =
          (typeof body.description === 'string' && body.description) ||
          (typeof body.message === 'string' && body.message) ||
          'Unable to add items to cart';
        showToast(msg, 'error');
        setAddSelectedButtonState(btn, 'idle');
        updateAddSelectedState(detailsRoot);
        return;
      }

      if (cartJsonUrl) {
        const cartRes = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          if (availabilityApi?.handleSuccessfulCartMutation) {
            await availabilityApi.handleSuccessfulCartMutation(cart, { reason: 'cart-add' });
          }
          if (window.aoneCartCount) {
            window.aoneCartCount.syncBadgeDom?.(
              typeof cart.item_count === 'number' ? cart.item_count : 0
            );
            // Prefer sync-only when inventory store already applied this cart.
            if (!availabilityApi?.handleSuccessfulCartMutation) {
              window.aoneCartCount.publish(cart, 'plp-details-variant-table');
            }
          }
        } else if (availabilityApi?.handleSuccessfulCartMutation) {
          await availabilityApi.handleSuccessfulCartMutation(null, { reason: 'cart-add' });
        }
      } else if (availabilityApi?.handleSuccessfulCartMutation) {
        await availabilityApi.handleSuccessfulCartMutation(null, { reason: 'cart-add' });
      }

      resetVariantTable(detailsRoot);
      // Premium multidimensional PDP rebuilds cells from shared inventory state.
      if (typeof window.aonePremiumPdpRehydrateInventory === 'function') {
        window.aonePremiumPdpRehydrateInventory();
      }
      await hydrateDetailsWmsAvailability(detailsRoot);
      setAddSelectedButtonState(btn, 'success');
    } catch (err) {
      console.error('[plp-table-details]', err);
      showToast('Unable to add items to cart', 'error');
      setAddSelectedButtonState(btn, 'idle');
      updateAddSelectedState(detailsRoot);
    }
  }

  /** @typedef {{ modal: HTMLElement, detailsRoot: HTMLElement, activeRow: HTMLElement | null, loadedOptions: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }>, selectedIds: string[], requestSeq: number, activeSku: string, insufficientAvailable: boolean }} WeightModalState */

  /** @type {WeakMap<HTMLElement, WeightModalState>} */
  const weightModalStates = new WeakMap();
  /** @type {WeakMap<HTMLElement, WeightModalState>} */
  const weightModalByModal = new WeakMap();

  /** @param {WeightModalState} state */
  function registerWeightModalState(state) {
    weightModalStates.set(state.detailsRoot, state);
    weightModalByModal.set(state.modal, state);
    state.modal.dataset.weightDetailsRootKey = String(state.detailsRoot.dataset.productId || '');
  }

  /** @param {HTMLElement} detailsRoot */
  function getWeightModalState(detailsRoot) {
    const cached = weightModalStates.get(detailsRoot);
    if (cached) {
      registerWeightModalState(cached);
      return cached;
    }

    const modal = /** @type {HTMLElement | null} */ (
      detailsRoot.querySelector('[data-order-sheet-weight-modal]')
    );
    if (!modal) return null;

    const state = {
      modal,
      detailsRoot,
      activeRow: null,
      loadedOptions: [],
      selectedIds: [],
      requestSeq: 0,
      activeSku: '',
      insufficientAvailable: false,
    };
    registerWeightModalState(state);
    return state;
  }

  /** @param {HTMLElement} modal */
  function getWeightModalStateFromModal(modal) {
    const cached = weightModalByModal.get(modal);
    if (cached) return cached;

    const productId = modal.dataset.weightDetailsRootKey;
    if (productId) {
      for (const host of document.querySelectorAll('[data-plp-expanded-details]')) {
        if (!(host instanceof HTMLElement)) continue;
        if (host.dataset.productId !== productId) continue;
        const state = weightModalStates.get(host);
        if (state && state.modal === modal) {
          registerWeightModalState(state);
          return state;
        }
      }
    }

    return null;
  }

  /** @param {WeightModalState} state */
  function portalWeightModal(state) {
    const modal = state.modal;
    if (modal.parentElement === document.body) return;
    if (!modal.dataset.weightModalAnchor) {
      const anchor = document.createComment('order-sheet-weight-modal-anchor');
      modal.parentNode?.insertBefore(anchor, modal);
      modal.dataset.weightModalAnchor = 'true';
      /** @type {WeightModalState & { modalAnchor?: Comment }} */ (state).modalAnchor = anchor;
    }
    document.body.appendChild(modal);
  }

  /** @param {WeightModalState} state */
  function restoreWeightModalToAnchor(state) {
    const modal = state.modal;
    const anchor = /** @type {WeightModalState & { modalAnchor?: Comment }} */ (state).modalAnchor;
    if (!(anchor instanceof Comment)) return;
    const parent = anchor.parentNode;
    if (!parent) return;
    if (modal.parentNode === parent && modal.previousSibling === anchor) return;
    parent.insertBefore(modal, anchor.nextSibling);
  }

  /** @param {WeightModalState} state */
  function getModalRequiredCount(state) {
    if (!state.activeRow) return 0;
    const els = getModalEls(state);
    if (els.qtyInput instanceof HTMLInputElement) return readQty(els.qtyInput);
    return getRowQty(state.activeRow);
  }

  /** @param {WeightModalState} state @param {number} qty */
  function syncModalQtyUi(state, qty) {
    const els = getModalEls(state);
    const safeQty = Math.max(0, qty);
    if (els.qtyInput instanceof HTMLInputElement) {
      const next = String(safeQty);
      if (els.qtyInput.value !== next) writeQty(els.qtyInput, safeQty, { silent: true });
    }
    if (els.qtyMinus instanceof HTMLButtonElement) els.qtyMinus.disabled = safeQty <= 0;
  }

  /**
   * @param {WeightModalState} state
   * @param {number} qty
   */
  function applyModalQtyChange(state, qty) {
    const safeQty = Math.max(0, qty);
    syncModalQtyUi(state, safeQty);

    if (!state.loadedOptions.length) return;

    state.insufficientAvailable = state.loadedOptions.length < safeQty;
    if (state.insufficientAvailable) {
      const template =
        state.modal.dataset.weightInsufficientMsg ||
        'Only {available} weight(s) available. You requested {required}.';
      showToast(
        template
          .replace(/\{available\}/g, String(state.loadedOptions.length))
          .replace(/\{required\}/g, String(safeQty)),
        'error'
      );
      state.selectedIds = [];
    } else if (state.selectedIds.length > safeQty) {
      const selectedEntries = state.selectedIds
        .map((id) => state.loadedOptions.find((opt) => opt.id === id))
        .filter(Boolean)
        .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
      state.selectedIds = selectedEntries.slice(0, safeQty).map((opt) => opt.id);
    } else if (state.selectedIds.length < safeQty && !state.insufficientAvailable) {
      const sortedOptions = state.loadedOptions
        .slice()
        .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
      for (const opt of sortedOptions) {
        if (state.selectedIds.length >= safeQty) break;
        if (state.selectedIds.indexOf(opt.id) < 0) state.selectedIds.push(opt.id);
      }
    }
    renderModalCheckboxes(state);
    updateModalSummary(state);
  }

  /** @param {WeightModalState} state */
  function getModalEls(state) {
    const m = state.modal;
    return {
      skuWrap: m.querySelector('[data-order-sheet-weight-sku-wrap]'),
      skuValue: m.querySelector('[data-order-sheet-weight-sku]'),
      qtyInput: /** @type {HTMLInputElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-input]')),
      qtyMinus: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-minus]')),
      qtyPlus: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-plus]')),
      fetchBtn: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-fetch]')),
      fetchLabel: m.querySelector('[data-order-sheet-weight-fetch-label]'),
      spinner: m.querySelector('[data-order-sheet-weight-spinner]'),
      status: m.querySelector('[data-order-sheet-weight-status]'),
      optionsWrap: m.querySelector('[data-order-sheet-weight-options-wrap]'),
      dropdown: m.querySelector('[data-order-sheet-weight-dropdown]'),
      dropdownPanel: m.querySelector('[data-order-sheet-weight-dropdown-panel]'),
      dropdownCount: m.querySelector('[data-order-sheet-weight-dropdown-count]'),
      dropdownSearch: /** @type {HTMLInputElement | null} */ (
        m.querySelector('[data-order-sheet-weight-dropdown-search]')
      ),
      dropdownEmpty: m.querySelector('[data-order-sheet-weight-dropdown-empty]'),
      loading: m.querySelector('[data-order-sheet-weight-loading]'),
      optionsList: m.querySelector('[data-order-sheet-weight-options]'),
      empty: m.querySelector('[data-order-sheet-weight-empty]'),
      summary: m.querySelector('[data-order-sheet-weight-summary]'),
      summaryQty: m.querySelector('[data-order-sheet-weight-summary-qty]'),
      summaryTotal: m.querySelector('[data-order-sheet-weight-summary-total]'),
      error: m.querySelector('[data-order-sheet-weight-error]'),
      confirm: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-confirm]')),
    };
  }

  /** @param {WeightModalState} state */
  function resetWeightSearch(state) {
    const els = getModalEls(state);
    if (els.dropdownSearch) els.dropdownSearch.value = '';
    if (els.dropdownEmpty) els.dropdownEmpty.hidden = true;
    if (els.optionsList) {
      els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((item) => {
        item.hidden = false;
      });
    }
  }

  /** @param {WeightModalState} state */
  function applyWeightFilter(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;

    const query = (els.dropdownSearch?.value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    let visible = 0;

    els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const haystack = String(item.dataset.searchText || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      const match = !query || haystack.startsWith(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (els.dropdownEmpty) {
      els.dropdownEmpty.hidden = visible > 0;
    }

    if (els.dropdownCount) {
      const total = els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').length;
      if (!total) {
        els.dropdownCount.textContent = '';
      } else if (query) {
        els.dropdownCount.textContent = visible + ' of ' + total;
      } else {
        els.dropdownCount.textContent = total + ' Total';
      }
    }
  }

  /** @param {WeightModalState} state @param {boolean} visible */
  function setWeightOptionsVisible(state, visible) {
    const els = getModalEls(state);
    if (!els.dropdownPanel) return;
    els.dropdownPanel.hidden = !visible;
    if (visible) {
      applyWeightFilter(state);
    } else {
      resetWeightSearch(state);
    }
  }

  /** @param {WeightModalState} state */
  function bindWeightSearch(state) {
    const search = getModalEls(state).dropdownSearch;
    if (!search || search.dataset.weightSearchBound === 'true') return;
    search.dataset.weightSearchBound = 'true';
    let timer = 0;

    search.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => applyWeightFilter(state), WEIGHT_SEARCH_DEBOUNCE_MS);
    });
    search.addEventListener('keydown', (e) => {
      e.stopPropagation();
    });
    search.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  /** @param {WeightModalState} state @param {boolean} disabled */
  function setModalInteractiveControlsDisabled(state, disabled) {
    const els = getModalEls(state);
    if (els.dropdownSearch) els.dropdownSearch.disabled = disabled;
    if (els.optionsList) {
      els.optionsList.querySelectorAll('button, input').forEach((node) => {
        if (
          node instanceof HTMLButtonElement ||
          node instanceof HTMLInputElement ||
          node instanceof HTMLSelectElement ||
          node instanceof HTMLTextAreaElement
        ) {
          node.disabled = disabled;
        }
      });
    }
    if (disabled && els.confirm) els.confirm.disabled = true;
  }

  /** @param {WeightModalState} state @param {boolean} usePlaceholder */
  function setModalSummaryPlaceholders(state, usePlaceholder) {
    const els = getModalEls(state);
    if (!els.summary || !els.summaryQty || !els.summaryTotal) return;
    if (usePlaceholder) {
      els.summary.textContent = '--';
      els.summaryQty.textContent = '--';
      els.summaryTotal.textContent = '--';
      return;
    }
    if (els.summary.textContent === '--') els.summary.textContent = '0';
    if (els.summaryQty.textContent === '--') els.summaryQty.textContent = '0';
    if (els.summaryTotal.textContent === '--') els.summaryTotal.textContent = '0.00g';
  }

  /** @param {WeightModalState} state @param {'idle'|'loading'|'success'|'empty'|'error'} uiState */
  function setModalFetchUi(state, uiState) {
    const els = getModalEls(state);
    if (!els.status) return;
    state.modal.dataset.fetchState = uiState;

    if (uiState === 'loading') {
      els.status.textContent = 'Fetching available weights...';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = false;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = true;
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, true);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    if (uiState === 'error') {
      els.status.textContent = '';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = true;
      if (els.error) els.error.hidden = false;
      setModalSummaryPlaceholders(state, true);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    if (uiState === 'success') {
      els.status.textContent = '';
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = false;
      if (els.empty) els.empty.hidden = true;
      setWeightOptionsVisible(state, true);
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, false);
      setModalInteractiveControlsDisabled(state, false);
      return;
    }
    if (uiState === 'empty') {
      els.status.textContent = '';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = false;
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, false);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    els.status.textContent = '';
    if (els.loading) els.loading.hidden = true;
    setWeightOptionsVisible(state, false);
    if (els.optionsWrap) els.optionsWrap.hidden = true;
    if (els.empty) els.empty.hidden = true;
    if (els.error) els.error.hidden = true;
    setModalSummaryPlaceholders(state, false);
    setModalInteractiveControlsDisabled(state, true);
  }

  /** @param {string} label */
  function parseWeightLabelGrams(label) {
    const m = String(label || '').match(/([\d.]+)/);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    return Number.isFinite(n) ? n : 0;
  }

  /** @param {WeightModalState} state */
  function buildWeightGroups(state) {
    /** @type {Map<string, { label: string, ids: string[], selectedCount: number, totalCount: number }>} */
    const map = new Map();
    const selectedSet = new Set(state.selectedIds);
    for (const opt of state.loadedOptions) {
      const label = String(opt.label || '').trim();
      if (!label) continue;
      if (!map.has(label)) {
        map.set(label, { label, ids: [], selectedCount: 0, totalCount: 0 });
      }
      const group = map.get(label);
      if (!group) continue;
      group.ids.push(opt.id);
      group.totalCount += 1;
      if (selectedSet.has(opt.id)) group.selectedCount += 1;
    }
    return Array.from(map.values());
  }

  /**
   * @param {WeightModalState} state
   * @returns {number}
   */
  function getModalMaxSelectable(state) {
    const rowInput = state.activeRow ? getRowQtyInput(state.activeRow) : null;
    const rowMax = state.activeRow ? getRowQtyMax(state.activeRow, rowInput || undefined) : null;
    const byStock = state.loadedOptions.length;
    if (rowMax == null) return byStock;
    return Math.max(0, Math.min(byStock, rowMax));
  }

  /**
   * @param {WeightModalState} state
   * @param {number} groupIndex
   * @param {number} desiredQty
   */
  function setWeightGroupSelection(state, groupIndex, desiredQty) {
    const maxSelectable = getModalMaxSelectable(state);
    const groups = buildWeightGroups(state);
    const group = groups[groupIndex];
    if (!group) return;

    const selectedSet = new Set(state.selectedIds);
    const selectedIdsInGroup = group.ids.filter((id) => selectedSet.has(id));

    let nextQty = Math.max(0, Math.floor(Number.isFinite(desiredQty) ? desiredQty : 0));
    nextQty = Math.min(nextQty, group.totalCount);

    const selectedOutsideGroup = state.selectedIds.length - selectedIdsInGroup.length;
    const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
    nextQty = Math.min(nextQty, maxForGroup);

    if (nextQty > selectedIdsInGroup.length) {
      const toAdd = nextQty - selectedIdsInGroup.length;
      let added = 0;
      for (const id of group.ids) {
        if (added >= toAdd) break;
        if (selectedSet.has(id)) continue;
        state.selectedIds.push(id);
        selectedSet.add(id);
        added += 1;
      }
    } else if (nextQty < selectedIdsInGroup.length) {
      const toRemove = selectedIdsInGroup.length - nextQty;
      let removed = 0;
      for (let i = state.selectedIds.length - 1; i >= 0; i -= 1) {
        if (removed >= toRemove) break;
        const id = state.selectedIds[i];
        if (group.ids.indexOf(id) < 0) continue;
        state.selectedIds.splice(i, 1);
        removed += 1;
      }
    }

    if (state.activeRow) {
      const rowInput = getRowQtyInput(state.activeRow);
      if (rowInput) writeQty(rowInput, state.selectedIds.length, { silent: true });
    }

    syncWeightRowsUi(state);
    updateModalSummary(state);
  }

  /** @param {WeightModalState} state */
  function updateModalSummary(state) {
    const els = getModalEls(state);
    if (!els.status || !els.confirm) return;

    const available = state.loadedOptions.length;
    const selected = state.selectedIds
      .map((id) => state.loadedOptions.find((o) => o.id === id))
      .filter(Boolean);
    const selectedCount = selected.length;
    const maxSelectable = getModalMaxSelectable(state);

    const uniqueWeights = Array.from(new Set(selected.map((o) => String(o.label || '').trim()).filter(Boolean)));
    const totalGrams = selected.reduce((sum, opt) => sum + parseWeightLabelGrams(String(opt.label || '')), 0);

    if (els.summary) {
      els.summary.textContent = String(uniqueWeights.length);
    }
    if (els.summaryQty) els.summaryQty.textContent = String(selected.length);
    if (els.summaryTotal) {
      els.summaryTotal.textContent = formatWeightTotal(totalGrams);
    }

    // Keep filtered visibility in sync when values change.
    applyWeightFilter(state);

    const canConfirm = !state.insufficientAvailable && selectedCount > 0 && state.loadedOptions.length > 0;

    els.confirm.disabled = !canConfirm;

    if (state.insufficientAvailable) {
      els.status.textContent = 'Only ' + available + ' available. Reduce selection.';
    } else {
      els.status.textContent = '';
    }
  }

  /** @param {WeightModalState} state */
  function renderModalCheckboxes(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;

    const maxSelectable = getModalMaxSelectable(state);

    const groups = buildWeightGroups(state);
    els.optionsList.innerHTML = '';
    if (!groups.length) {
      setModalFetchUi(state, 'empty');
      return;
    }
    if (els.empty) els.empty.hidden = true;
    if (els.optionsWrap) els.optionsWrap.hidden = false;
    if (groups.length) {
      const head = document.createElement('div');
      head.className = 'order-sheet-weight-modal__list-head';
      head.innerHTML = `
        <span>WEIGHT</span>
        <span>AVAILABLE QTY</span>
        <span>SELECTED QTY</span>
      `;
      els.optionsList.appendChild(head);
    }
    if (els.dropdownEmpty) {
      els.dropdownEmpty.textContent = 'No matching weights found';
      els.dropdownEmpty.hidden = true;
    }

    groups.forEach((group, idx) => {
      const row = document.createElement('div');
      row.className = 'order-sheet-weight-modal__checkbox-item';
      row.dataset.searchText = String(group.label || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      row.dataset.groupIndex = String(idx);
      if (group.selectedCount > 0) row.classList.add('is-selected');

      const left = document.createElement('div');
      left.className = 'order-sheet-weight-modal__weight-main';
      left.innerHTML = `
        <strong class="order-sheet-weight-modal__weight-value">${escapeHtml(formatWeightLabelForDisplay(group.label))}</strong>
      `;

      const center = document.createElement('div');
      center.className = 'order-sheet-weight-modal__stock-meta';
      center.innerHTML = `
        <span class="order-sheet-weight-modal__stock-count">${group.totalCount} In Stock</span>
      `;

      const step = document.createElement('div');
      step.className = 'order-sheet-weight-modal__row-stepper';
      const selectedOutsideGroup = state.selectedIds.length - group.selectedCount;
      const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
      const canInc = group.selectedCount < Math.min(group.totalCount, maxForGroup);
      step.innerHTML = `
        <button type="button" class="order-sheet-weight-modal__row-step-btn" data-order-sheet-weight-row-minus ${
          group.selectedCount <= 0 ? 'disabled' : ''
        } aria-label="Decrease ${escapeHtml(group.label)} selection">−</button>
        <input
          type="number"
          class="order-sheet-weight-modal__row-step-input"
          value="${group.selectedCount}"
          min="0"
          max="${Math.min(group.totalCount, maxForGroup)}"
          step="1"
          inputmode="numeric"
          aria-label="Selected quantity for ${escapeHtml(group.label)}"
          data-order-sheet-weight-row-input
        >
        <button type="button" class="order-sheet-weight-modal__row-step-btn" data-order-sheet-weight-row-plus ${
          canInc ? '' : 'disabled'
        } aria-label="Increase ${escapeHtml(group.label)} selection">+</button>
      `;

      row.appendChild(left);
      row.appendChild(center);
      row.appendChild(step);
      els.optionsList.appendChild(row);
    });

    syncWeightRowsUi(state);
    applyWeightFilter(state);
  }

  /** @param {WeightModalState} state */
  function syncWeightRowsUi(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;
    const groups = buildWeightGroups(state);
    const maxSelectable = getModalMaxSelectable(state);

    els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const groupIndex = Number(row.dataset.groupIndex);
      if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
      const group = groups[groupIndex];
      if (!group) return;

      const selectedOutsideGroup = state.selectedIds.length - group.selectedCount;
      const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
      const maxAllowedForGroup = Math.min(group.totalCount, maxForGroup);
      const canInc = group.selectedCount < maxAllowedForGroup;

      row.classList.toggle('is-selected', group.selectedCount > 0);
      const countEl = row.querySelector('.order-sheet-weight-modal__stock-count');
      if (countEl) {
        countEl.textContent = `${group.totalCount} In Stock`;
      }

      const rowInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-input]')
      );
      if (rowInput) {
        rowInput.value = String(group.selectedCount);
        rowInput.max = String(maxAllowedForGroup);
      }
      const minusBtn = /** @type {HTMLButtonElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-minus]')
      );
      if (minusBtn) minusBtn.disabled = group.selectedCount <= 0;
      const plusBtn = /** @type {HTMLButtonElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-plus]')
      );
      if (plusBtn) plusBtn.disabled = !canInc;

    });
  }

  /** @param {WeightModalState} state */
  function submitWeightFetch(state) {
    if (!state.activeRow) return;
    const sku = getRowSku(state.activeRow);
    if (!sku) {
      setModalFetchUi(state, 'error');
      return;
    }

    const runId = ++state.requestSeq;
    state.activeSku = sku;
    state.insufficientAvailable = false;
    setModalFetchUi(state, 'loading');

    const els = getModalEls(state);
    if (els.status) els.status.textContent = 'Fetching available weights...';

    const availabilityApi = getWmsAvailabilityApi();
    const cartReady = availabilityApi?.ensureCartSerialsSynced
      ? availabilityApi.ensureCartSerialsSynced(false).catch(() => null)
      : Promise.resolve(null);

    Promise.resolve(cartReady)
      .then(() => fetchWeightPayload(state.detailsRoot, sku))
      .then((data) => {
        if (runId !== state.requestSeq || state.activeSku !== sku) return;

        const detailsRoot = state.detailsRoot;
        const exclude =
          state.activeRow instanceof HTMLElement
            ? collectSelectedSerialsOutsideRow(detailsRoot, state.activeRow)
            : new Set();
        /** @type {Set<string>} */
        const keepSerials = new Set(
          state.activeRow
            ? String(state.activeRow.dataset.serialNumbersValue || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : []
        );

        state.loadedOptions = (data.entries || [])
          .filter((entry) => {
            const serial = String(entry?.serialNumber || '').trim();
            if (!serial) return false;
            if (keepSerials.has(serial)) return true;
            if (exclude.has(serial)) return false;
            if (
              availabilityApi &&
              typeof availabilityApi.isSerialReserved === 'function' &&
              availabilityApi.isSerialReserved(serial)
            ) {
              return false;
            }
            return true;
          })
          .slice()
          .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
        const existingQty = state.activeRow ? getRowQty(state.activeRow) : 0;
        const presetQty = Math.max(0, Math.floor(existingQty));
        state.insufficientAvailable = false;

        if (presetQty > 0) {
          /** @type {string[]} */
          const preferredSerials = Array.from(keepSerials);
          /** @type {string[]} */
          const preferredLabels = state.activeRow
            ? String(state.activeRow.dataset.weightValue || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
          const maxSelectable = Math.min(
            Math.max(0, getModalMaxSelectable(state)),
            state.loadedOptions.length,
            presetQty
          );
          /** @type {string[]} */
          const selectedIds = [];
          const usedIds = new Set();

          preferredSerials.forEach((serial) => {
            if (selectedIds.length >= maxSelectable) return;
            const match = state.loadedOptions.find(
              (opt) =>
                !usedIds.has(opt.id) &&
                opt.serialNumber != null &&
                String(opt.serialNumber).trim() === serial
            );
            if (!match) return;
            selectedIds.push(match.id);
            usedIds.add(match.id);
          });

          preferredLabels.forEach((label) => {
            if (selectedIds.length >= maxSelectable) return;
            const match = state.loadedOptions.find(
              (opt) => !usedIds.has(opt.id) && String(opt.label || '').trim() === label
            );
            if (!match) return;
            selectedIds.push(match.id);
            usedIds.add(match.id);
          });

          if (selectedIds.length < maxSelectable) {
            state.loadedOptions.forEach((opt) => {
              if (selectedIds.length >= maxSelectable) return;
              if (usedIds.has(opt.id)) return;
              selectedIds.push(opt.id);
              usedIds.add(opt.id);
            });
          }
          state.selectedIds = selectedIds;
        } else {
          state.selectedIds = [];
        }

        if (!state.loadedOptions.length) {
          setModalFetchUi(state, 'empty');
          updateModalSummary(state);
          return;
        }

        setModalFetchUi(state, 'success');
        renderModalCheckboxes(state);
        updateModalSummary(state);
      })
      .catch((err) => {
        if (runId !== state.requestSeq || state.activeSku !== sku) return;
        state.loadedOptions = [];
        state.selectedIds = [];
        state.insufficientAvailable = false;
        setModalFetchUi(state, 'error');
        const message =
          err && typeof err.message === 'string' && err.message.indexOf('Weight information') >= 0
            ? err.message
            : 'Weight information is currently unavailable. Please try again.';
        showToast(message, 'error');
      });
  }

  /**
   * @param {unknown} productType
   * @returns {boolean}
   */
  function isChainProductType(productType) {
    const normalized = normalizeKey(productType);
    return normalized === 'chain' || normalized === 'chains';
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @param {Record<string, unknown> | null | undefined} [product]
   * @returns {boolean}
   */
  function isChainProductContext(detailsRoot, product) {
    const datasetFlag = detailsRoot.dataset.isChainProduct;
    if (datasetFlag === 'true') return true;
    if (datasetFlag === 'false') return false;
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    if (matrixHost instanceof HTMLElement && matrixHost.dataset.productType) {
      return isChainProductType(matrixHost.dataset.productType);
    }
    if (product && typeof product === 'object') {
      return isChainProductType(product.product_type || product.type);
    }
    return false;
  }

  /**
   * @param {unknown} value
   * @param {string[]} suffixes
   */
  function stripUnitSuffix(value, suffixes) {
    let text = normalizeString(value);
    if (!text) return '';
    suffixes.forEach((suffix) => {
      const re = new RegExp(`\\s*${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      text = text.replace(re, '').trim();
    });
    return text.replace(/["']/g, '').trim();
  }

  /** @param {unknown} value */
  function formatModalWidth(value) {
    const raw = stripUnitSuffix(value, ['mm', 'millimeter', 'millimeters']);
    if (!raw) return '';
    return `${raw} MM`;
  }

  /** @param {unknown} value */
  function formatModalLength(value) {
    const raw = stripUnitSuffix(value, ['inches', 'inch', 'in']);
    if (!raw) return '';
    return `${raw} Inches`;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   * @param {number} variantId
   */
  function findProductVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !Number.isFinite(variantId) || variantId <= 0) {
      return null;
    }
    return (
      /** @type {Record<string, unknown> | undefined} */ (
        product.variants.find((entry) => Number(entry && entry.id) === variantId)
      ) || null
    );
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @param {HTMLElement} row
   * @param {HTMLButtonElement | null | undefined} [trigger]
   */
  function getMatrixContextFromRow(detailsRoot, row, trigger) {
    const product = parseDetailsProductJson(detailsRoot);
    const variantId = Number(row.dataset.variantId);
    const variant = findProductVariant(product, variantId);
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    const productType =
      (matrixHost instanceof HTMLElement && matrixHost.dataset.productType) ||
      (product && (product.product_type || product.type)) ||
      detailsRoot.dataset.productType ||
      '';

    let width =
      (trigger instanceof HTMLElement && trigger.dataset.matrixWidth) ||
      row.dataset.matrixWidth ||
      '';
    let length =
      (trigger instanceof HTMLElement && trigger.dataset.matrixLength) ||
      row.dataset.matrixLength ||
      '';

    if ((!width || !length) && variant && product) {
      const { widthIdx, lengthIdx } = resolveChainAxes(product);
      width = width || getVariantOptionValue(variant, widthIdx);
      length = length || getVariantOptionValue(variant, lengthIdx);
    }

    return {
      variantId,
      sku: normalizeString(row.dataset.variantSku || (variant && variant.sku)),
      width,
      length,
      productType: normalizeString(productType),
      triggerElement: trigger || null,
    };
  }

  /**
   * @param {HTMLElement} modal
   * @param {{ width?: string, length?: string, productType?: string }} context
   * @param {HTMLElement} detailsRoot
   */
  function updateWeightModalDimensions(modal, context, detailsRoot) {
    const dimensionsWrap = modal.querySelector('[data-weight-modal-dimensions]');
    const widthRow = modal.querySelector('[data-dimension="width"]');
    const lengthRow = modal.querySelector('[data-dimension="length"]');
    const widthValue = modal.querySelector('[data-modal-width]');
    const lengthValue = modal.querySelector('[data-modal-length]');
    const showDimensions =
      isChainProductType(context.productType) ||
      isChainProductContext(detailsRoot, parseDetailsProductJson(detailsRoot));

    if (dimensionsWrap instanceof HTMLElement) dimensionsWrap.hidden = !showDimensions;
    if (widthRow instanceof HTMLElement) widthRow.hidden = !showDimensions;
    if (lengthRow instanceof HTMLElement) lengthRow.hidden = !showDimensions;

    if (!showDimensions) {
      if (widthValue instanceof HTMLElement) widthValue.textContent = '';
      if (lengthValue instanceof HTMLElement) lengthValue.textContent = '';
      return;
    }

    const formattedWidth = formatModalWidth(context.width);
    const formattedLength = formatModalLength(context.length);
    if (widthValue instanceof HTMLElement) widthValue.textContent = formattedWidth;
    if (lengthValue instanceof HTMLElement) lengthValue.textContent = formattedLength;

    if (!formattedWidth || !formattedLength) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[chain-matrix] missing width/length for weight modal', {
          variantId: context.variantId || context.sku,
          width: context.width,
          length: context.length,
        });
      }
    }
  }

  /** @param {HTMLElement} detailsRoot @param {HTMLElement} row */
  function syncWeightModalProductImage(detailsRoot, row) {
    const modal = detailsRoot.querySelector('[data-order-sheet-weight-modal]');
    if (!(modal instanceof HTMLElement)) return;
    const imageEl = modal.querySelector('.order-sheet-weight-modal__product-image');
    if (!(imageEl instanceof HTMLImageElement)) return;

    const product = parseDetailsProductJson(detailsRoot);
    const variantId = Number(row.dataset.variantId);
    const variantSrc = getVariantImageSrc(product, variantId);
    const productSrc = getProductFallbackImageSrc(product);
    const finalSrc = variantSrc || productSrc || WEIGHT_MODAL_FALLBACK_IMAGE;
    const altText = normalizeString(product && product.title) || 'Product image';
    const requestToken = String(Date.now()) + '-' + String(variantId || 0);
    imageEl.dataset.modalImageRequest = requestToken;

    imageEl.src = WEIGHT_MODAL_FALLBACK_IMAGE;
    imageEl.alt = altText;
    imageEl.classList.add('order-sheet-weight-modal__product-image--placeholder');
    imageEl.decoding = 'async';
    imageEl.loading = 'eager';

    if (finalSrc === WEIGHT_MODAL_FALLBACK_IMAGE) return;

    const preload = new Image();
    preload.decoding = 'async';
    preload.onload = function () {
      if (imageEl.dataset.modalImageRequest !== requestToken) return;
      imageEl.src = finalSrc;
      imageEl.alt = altText;
      imageEl.classList.remove('order-sheet-weight-modal__product-image--placeholder');
    };
    preload.onerror = function () {
      if (imageEl.dataset.modalImageRequest !== requestToken) return;
      imageEl.src = WEIGHT_MODAL_FALLBACK_IMAGE;
      imageEl.alt = altText;
      imageEl.classList.add('order-sheet-weight-modal__product-image--placeholder');
    };
    preload.src = finalSrc;
  }

  /** @param {HTMLElement} detailsRoot @param {HTMLElement} row @param {HTMLButtonElement | null} [trigger] */
  function openWeightModal(detailsRoot, row, trigger) {
    if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return;
    const wmsStatus = String(row.dataset.wmsStatus || '');
    if (wmsStatus === 'coming-soon' || wmsStatus === 'all-in-cart' || wmsStatus === 'loading') return;

    const state = getWeightModalState(detailsRoot);
    if (!state) return;

    const weightTrigger =
      trigger instanceof HTMLButtonElement
        ? trigger
        : /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));

    state.activeRow = row;
    state.loadedOptions = [];
    state.selectedIds = [];
    state.requestSeq += 1;
    state.activeSku = '';
    state.insufficientAvailable = false;
    syncWeightModalProductImage(detailsRoot, row);

    const modalMeta = state.modal.querySelector('.order-sheet-weight-modal__product-meta');
    const matrixContext = getMatrixContextFromRow(detailsRoot, row, weightTrigger);
    updateWeightModalDimensions(state.modal, matrixContext, detailsRoot);
    if (modalMeta instanceof HTMLElement && !state.modal.querySelector('[data-weight-modal-dimensions]')) {
      const formattedWidth = formatModalWidth(matrixContext.width);
      const formattedLength = formatModalLength(matrixContext.length);
      if (isChainProductContext(detailsRoot, parseDetailsProductJson(detailsRoot))) {
        modalMeta.innerHTML = `<span>Length: ${escapeHtml(formattedLength || '—')}</span><span aria-hidden="true">•</span><span>Width: ${escapeHtml(formattedWidth || '—')}</span>`;
      } else {
        modalMeta.innerHTML = '';
        modalMeta.hidden = true;
      }
    }

    const els = getModalEls(state);

    if (els.status) els.status.textContent = '';
    if (els.optionsList) els.optionsList.innerHTML = '';
    if (els.summary) els.summary.textContent = '0';
    if (els.summaryQty) els.summaryQty.textContent = '0';
    if (els.summaryTotal) els.summaryTotal.textContent = '0.00g';
    if (els.dropdownCount) els.dropdownCount.textContent = '';
    if (els.dropdownSearch) els.dropdownSearch.value = '';
    if (els.dropdownEmpty) els.dropdownEmpty.hidden = true;
    if (els.loading) els.loading.hidden = true;
    if (els.empty) els.empty.hidden = true;
    setWeightOptionsVisible(state, false);
    if (els.confirm) els.confirm.disabled = true;
    state.modal.querySelectorAll('[data-order-sheet-weight-close]').forEach((node) => {
      if (!(node instanceof HTMLButtonElement)) return;
      const text = String(node.textContent || '').trim().toLowerCase();
      if (!text || text === '×' || text === 'x') return;
      node.textContent = 'Clear';
      node.dataset.orderSheetWeightClear = 'true';
    });

    setModalFetchUi(state, 'idle');
    registerWeightModalState(state);
    bindWeightSearch(state);
    portalWeightModal(state);
    state.modal.hidden = false;
    document.body.classList.add('order-sheet-weight-modal-open');
    submitWeightFetch(state);
    if (els.dropdownSearch) els.dropdownSearch.focus();
  }

  /** @param {HTMLElement} detailsRoot */
  function closeWeightModal(detailsRoot) {
    const state = getWeightModalState(detailsRoot);
    if (!state || state.modal.hidden) return;
    updateWeightModalDimensions(state.modal, { width: '', length: '', productType: '' }, detailsRoot);
    state.modal.hidden = true;
    state.activeRow = null;
    state.loadedOptions = [];
    state.selectedIds = [];
    state.requestSeq += 1;
    setWeightOptionsVisible(state, false);
    restoreWeightModalToAnchor(state);
    document.body.classList.remove('order-sheet-weight-modal-open');
  }

  /** @param {WeightModalState} state */
  function clearWeightModalSelection(state) {
    if (!state.activeRow) {
      closeWeightModal(state.detailsRoot);
      return;
    }
    const row = state.activeRow;
    const rowInput = getRowQtyInput(row);
    if (rowInput) writeQty(rowInput, 0, { silent: true });
    clearRowWeight(row);
    syncRowMinusState(row);

    const panel = state.detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    if (panel instanceof HTMLElement) syncBulkMap(panel, row);
    updateAddSelectedState(state.detailsRoot);
    notifyQtyUpdated(state.detailsRoot);
    updateDetailsSummary(state.detailsRoot);

    state.selectedIds = [];
    updateModalSummary(state);
    closeWeightModal(state.detailsRoot);
  }

  /** @param {WeightModalState} state */
  function confirmWeightModal(state) {
    if (!state.activeRow || state.insufficientAvailable) return;
    const selectedCount = state.selectedIds.length;
    if (selectedCount <= 0) {
      showToast('Set quantity greater than 0 before confirming.', 'error');
      return;
    }

    const selectedOptions = state.selectedIds
      .map((id) => state.loadedOptions.find((o) => o.id === id))
      .filter(Boolean);
    const selectedLabels = selectedOptions.map((o) => o.label);
    const selectedSerials = selectedOptions.map((o) =>
      o.serialNumber != null && String(o.serialNumber).trim() ? String(o.serialNumber).trim() : ''
    );

    if (selectedSerials.some((s) => !s)) {
      showToast(
        'Selected weights are missing serial numbers. Fetch weight again — if this persists, contact support.',
        'error'
      );
      return;
    }

    const row = state.activeRow;
    const rowInput = getRowQtyInput(row);
    if (rowInput) writeQty(rowInput, selectedCount, { silent: true });

    const panel = state.detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    syncRowMinusState(row);
    updateAddSelectedState(state.detailsRoot);

    setRowWeightValue(row, selectedLabels.join(', '), selectedSerials.join(', '));
    if (panel instanceof HTMLElement) syncBulkMap(panel, row);
    notifyQtyUpdated(state.detailsRoot);
    updateDetailsSummary(state.detailsRoot);
    closeWeightModal(state.detailsRoot);
  }

  /** @type {{ dialog: HTMLDialogElement, stage: HTMLElement, main: HTMLImageElement, thumbs: HTMLElement, prev: HTMLButtonElement, next: HTMLButtonElement, count: HTMLElement } | null} */
  let imageViewer = null;
  /** @type {{ src: string, alt: string }[]} */
  let imageViewerItems = [];
  let imageViewerIndex = 0;
  let imageViewerBodyOverflow = '';
  /** @type {HTMLElement | null} */
  let imageViewerTrigger = null;

  function ensureImageViewer() {
    if (imageViewer) return imageViewer;

    const dialog = document.createElement('dialog');
    dialog.className = 'aone-image-viewer';
    dialog.setAttribute('aria-label', 'Product image viewer');
    dialog.innerHTML = `
      <div class="aone-image-viewer__panel">
        <button type="button" class="aone-image-viewer__close" data-image-viewer-close aria-label="Close image viewer">×</button>
        <div class="aone-image-viewer__stage">
          <button type="button" class="aone-image-viewer__nav aone-image-viewer__nav--prev" data-image-viewer-prev aria-label="Previous image">‹</button>
          <img class="aone-image-viewer__image" alt="">
          <button type="button" class="aone-image-viewer__nav aone-image-viewer__nav--next" data-image-viewer-next aria-label="Next image">›</button>
        </div>
        <p class="aone-image-viewer__count" aria-live="polite"></p>
        <div class="aone-image-viewer__thumbs" role="list" aria-label="Product images"></div>
      </div>`;
    document.body.appendChild(dialog);

    const stage = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__stage'));
    const main = /** @type {HTMLImageElement | null} */ (dialog.querySelector('.aone-image-viewer__image'));
    const thumbs = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__thumbs'));
    const prev = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-image-viewer-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-image-viewer-next]'));
    const count = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__count'));
    if (!stage || !main || !thumbs || !prev || !next || !count) return null;

    const close = () => dialog.close();
    dialog.querySelector('[data-image-viewer-close]')?.addEventListener('click', close);
    prev.addEventListener('click', () => renderImageViewer(imageViewerIndex - 1));
    next.addEventListener('click', () => renderImageViewer(imageViewerIndex + 1));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        renderImageViewer(imageViewerIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        renderImageViewer(imageViewerIndex + 1);
      }
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = imageViewerBodyOverflow;
      imageViewerTrigger?.focus();
      imageViewerTrigger = null;
    });

    imageViewer = { dialog, stage, main, thumbs, prev, next, count };
    return imageViewer;
  }

  /** @param {number} requestedIndex */
  function renderImageViewer(requestedIndex) {
    const viewer = ensureImageViewer();
    if (!viewer || !imageViewerItems.length) return;
    const length = imageViewerItems.length;
    imageViewerIndex = ((requestedIndex % length) + length) % length;
    const item = imageViewerItems[imageViewerIndex];
    if (!item) return;
    viewer.main.removeAttribute('width');
    viewer.main.removeAttribute('height');
    viewer.main.src = item.src;
    viewer.main.alt = item.alt;
    viewer.count.textContent = `${imageViewerIndex + 1} / ${length}`;
    viewer.prev.hidden = length < 2;
    viewer.next.hidden = length < 2;
    viewer.stage.classList.toggle('aone-image-viewer__stage--single', length < 2);
    viewer.thumbs.querySelectorAll('[data-image-viewer-thumb]').forEach((thumb, index) => {
      thumb.classList.toggle('is-active', index === imageViewerIndex);
      thumb.setAttribute('aria-current', index === imageViewerIndex ? 'true' : 'false');
    });
  }

  /**
   * @param {Array<string | { src?: string, alt?: string }>} images
   * @param {number} [activeIndex]
   * @param {HTMLElement | null} [trigger]
   */
  function openImageViewer(images, activeIndex = 0, trigger = null) {
    const normalized = images
      .map((item) =>
        typeof item === 'string'
          ? { src: item, alt: '' }
          : { src: String(item?.src || ''), alt: String(item?.alt || '') }
      )
      .filter((item) => item.src);
    if (!normalized.length) return;

    const viewer = ensureImageViewer();
    if (!viewer) return;
    imageViewerItems = normalized;
    imageViewerTrigger = trigger;
    viewer.thumbs.innerHTML = '';
    normalized.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'aone-image-viewer__thumb';
      button.setAttribute('data-image-viewer-thumb', '');
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-label', `View image ${index + 1}`);
      const image = document.createElement('img');
      image.src = item.src;
      image.alt = item.alt;
      image.loading = 'lazy';
      button.appendChild(image);
      button.addEventListener('click', () => renderImageViewer(index));
      viewer.thumbs.appendChild(button);
    });
    renderImageViewer(activeIndex);
    imageViewerBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (!viewer.dialog.open) viewer.dialog.showModal();
  }

  /** @param {HTMLElement} gallery */
  function ensureGalleryViewButton(gallery) {
    gallery.querySelectorAll('.plp-exp-media__zoom').forEach((zoom) => zoom.remove());
    let button = gallery.querySelector('[data-plp-view-images]');
    const legacyLink = gallery.querySelector('.plp-exp-media__view-link');
    if (!button && legacyLink) {
      button = document.createElement('button');
      legacyLink.replaceWith(button);
    }
    if (!button) {
      button = document.createElement('button');
      gallery.querySelector('.plp-exp-media__main')?.insertAdjacentElement('afterend', button);
    }
    if (!(button instanceof HTMLButtonElement)) return;
    button.type = 'button';
    button.className = 'plp-exp-media__view-button';
    button.setAttribute('data-plp-view-images', '');
    button.setAttribute('aria-label', 'View product images');
    button.innerHTML = `
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.7"/>
        <circle cx="9" cy="10" r="2" stroke="currentColor" stroke-width="1.7"/>
        <path d="m5 18 5-5 3 3 2-2 4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>View Images</span>`;
  }

  /**
   * Resolve a usable image URL from an <img> (handles srcset-only / lazy cases).
   * @param {HTMLImageElement | null} img
   * @returns {string}
   */
  function resolveImageSrc(img) {
    if (!(img instanceof HTMLImageElement)) return '';
    const fromAttr =
      img.getAttribute('data-thumb-src') ||
      img.getAttribute('data-image-src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('src') ||
      '';
    if (fromAttr) return fromAttr;
    if (img.currentSrc) return img.currentSrc;
    const srcset = img.getAttribute('srcset') || '';
    if (!srcset) return '';
    const first = srcset.split(',')[0]?.trim().split(/\s+/)[0] || '';
    return first;
  }

  /** @param {HTMLElement} gallery */
  function getGalleryViewerData(gallery) {
    const popupItems = Array.from(
      gallery.querySelectorAll('[data-qvm-popup-image], [data-plp-popup-image]')
    );
    const popupImages = popupItems
      .map((item) => ({
        src: item.getAttribute('data-image-src') || '',
        alt: item.getAttribute('data-image-alt') || '',
      }))
      .filter((item) => item.src);
    if (popupImages.length) {
      const activePopup = gallery.querySelector(
        '[data-qvm-popup-image][data-active-image], [data-plp-popup-image][data-active-image]'
      );
      const activeSrc = activePopup?.getAttribute('data-image-src') || '';
      const activeIndex = Math.max(0, popupImages.findIndex((item) => item.src === activeSrc));
      return { images: popupImages, activeIndex };
    }

    const thumbs = Array.from(gallery.querySelectorAll('[data-plp-exp-thumb]'));
    const images = thumbs
      .map((thumb) => ({
        src: thumb.getAttribute('data-thumb-src') || resolveImageSrc(thumb.querySelector('img')),
        alt: thumb.querySelector('img')?.getAttribute('alt') || '',
      }))
      .filter((item) => item.src);
    const activeIndex = Math.max(
      0,
      thumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
    );
    if (images.length) return { images, activeIndex };

    // Single-image products omit thumbnails — fall back to the main stage image.
    const main =
      gallery.querySelector('[data-plp-exp-main-img]') ||
      gallery.querySelector('[data-plp-exp-main-media] img.plp-exp-media__img') ||
      gallery.querySelector('[data-plp-exp-main-media] img') ||
      gallery.querySelector('.plp-exp-media__img');
    const mainSrc =
      gallery.getAttribute('data-gallery-src') ||
      (main instanceof HTMLImageElement ? resolveImageSrc(main) : '');
    const mainAlt = main instanceof HTMLImageElement ? main.getAttribute('alt') || '' : '';
    return {
      images: mainSrc ? [{ src: mainSrc, alt: mainAlt }] : [],
      activeIndex: 0,
    };
  }

  /** @param {HTMLElement} panel */
  function bindPanel(panel) {
    if (panel.dataset.plpDetailsBound === 'true') return;
    panel.dataset.plpDetailsBound = 'true';

    const details = panel.querySelector('[data-plp-expanded-details]');
    if (!details) return;
    // Quick View owns its matrix HTML + WMS/cart reconciliation. Running hydrate here
    // races QV open and can leave Available Stock positive while qty/weight stay disabled.
    const isQuickViewPanel =
      panel.hasAttribute('data-quick-view-details-panel') ||
      (details instanceof HTMLElement && details.hasAttribute('data-quick-view-details'));
    if (!isQuickViewPanel) {
      buildChainMatrix(/** @type {HTMLElement} */ (details));
      hydrateDetailsWmsAvailability(/** @type {HTMLElement} */ (details));
    }
    panel.querySelectorAll('[data-plp-exp-media]').forEach((gallery) => {
      if (gallery instanceof HTMLElement) ensureGalleryViewButton(gallery);
    });

    const weightState = getWeightModalState(/** @type {HTMLElement} */ (details));
    if (weightState) {
      bindWeightSearch(weightState);
      const modal = weightState.modal;
      if (modal.dataset.orderSheetWeightModalBound !== 'true') {
        modal.dataset.orderSheetWeightModalBound = 'true';

      modal.addEventListener('click', (e) => {
        const closeEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-close]') : null;
        const retryEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-retry]') : null;
        const confirmEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-confirm]') : null;
        const rowMinus =
          e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-row-minus]') : null;
        const rowPlus =
          e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-row-plus]') : null;

        if (closeEl) {
          e.preventDefault();
          if (
            closeEl instanceof HTMLButtonElement &&
            closeEl.dataset.orderSheetWeightClear === 'true'
          ) {
            clearWeightModalSelection(weightState);
          } else {
            closeWeightModal(/** @type {HTMLElement} */ (details));
          }
          return;
        }
        if (rowMinus || rowPlus) {
          e.preventDefault();
          const rowEl = e.target instanceof Element ? e.target.closest('[data-group-index]') : null;
          if (!(rowEl instanceof HTMLElement)) return;
          const groupIndex = Number(rowEl.dataset.groupIndex);
          if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
          const input = /** @type {HTMLInputElement | null} */ (
            rowEl.querySelector('[data-order-sheet-weight-row-input]')
          );
          const current = input ? readQty(input) : 0;
          const next = rowPlus ? current + 1 : Math.max(0, current - 1);
          setWeightGroupSelection(weightState, groupIndex, next);
          return;
        }
        if (retryEl) {
          e.preventDefault();
          submitWeightFetch(weightState);
          return;
        }
        if (confirmEl) {
          e.preventDefault();
          confirmWeightModal(weightState);
        }
      });

      modal.addEventListener('input', (e) => {
        const input =
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-order-sheet-weight-row-input]')
            ? e.target
            : null;
        if (!input) return;
        const rowEl = input.closest('[data-group-index]');
        if (!(rowEl instanceof HTMLElement)) return;
        const groupIndex = Number(rowEl.dataset.groupIndex);
        if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
        setWeightGroupSelection(weightState, groupIndex, readQty(input));
      });

      modal.addEventListener('change', (e) => {
        const input =
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-order-sheet-weight-row-input]')
            ? e.target
            : null;
        if (!input) return;
        const rowEl = input.closest('[data-group-index]');
        if (!(rowEl instanceof HTMLElement)) return;
        const groupIndex = Number(rowEl.dataset.groupIndex);
        if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
        setWeightGroupSelection(weightState, groupIndex, readQty(input));
      });
      }
    }

    panel.addEventListener('click', (e) => {
      const viewImages = /** @type {HTMLElement | null} */ (
        e.target instanceof Element ? e.target.closest('[data-plp-view-images]') : null
      );
      if (viewImages && panel.contains(viewImages)) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = viewImages.closest('[data-plp-exp-media]');
        if (gallery instanceof HTMLElement) {
          const { images, activeIndex } = getGalleryViewerData(gallery);
          openImageViewer(images, activeIndex, viewImages);
        }
        return;
      }

      const thumb = /** @type {HTMLElement | null} */ (
        e.target instanceof Element ? e.target.closest('[data-plp-exp-thumb]') : null
      );
      if (thumb && panel.contains(thumb)) {
        e.stopPropagation();
        const src = thumb.getAttribute('data-thumb-src');
        const mainWrap = panel.querySelector('[data-plp-exp-main-media]');
        if (src && mainWrap) {
          panel.querySelectorAll('[data-plp-exp-thumb]').forEach((t) => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
          const mediaType = thumb.getAttribute('data-media-type');
          if (mediaType === 'image' || !mediaType || mediaType === 'model') {
            mainWrap.innerHTML = `<img class="plp-exp-media__img" src="${escapeHtml(src)}" alt="" loading="lazy" data-plp-exp-main-img />`;
          }
        }
        return;
      }

      const weightOpen = e.target instanceof Element ? e.target.closest('[data-details-weight-open]') : null;

      if (weightOpen instanceof HTMLButtonElement && details.contains(weightOpen)) {
        e.preventDefault();
        e.stopPropagation();
        if (weightOpen.disabled) return;
        const row = weightOpen.closest('[data-details-variant-row]');
        if (!(row instanceof HTMLElement) || !isRowAvailable(row)) return;
        openWeightModal(/** @type {HTMLElement} */ (details), row, weightOpen);
        return;
      }

      const clickEl =
        e.target instanceof Element ? e.target : e.target instanceof Node ? e.target.parentElement : null;
      const minusBtn = clickEl ? clickEl.closest('[data-details-qty-minus]') : null;
      const plusBtn = clickEl ? clickEl.closest('[data-details-qty-plus]') : null;
      const addBtn = clickEl ? clickEl.closest('[data-details-add-selected]') : null;

      if (minusBtn || plusBtn) {
        e.preventDefault();
        e.stopPropagation();
        const trigger = minusBtn || plusBtn;
        const row = trigger instanceof Element ? trigger.closest('[data-details-variant-row]') : null;
        if (!(row instanceof HTMLElement) || !details.contains(row) || !isRowAvailable(row)) return;
        if (isRowQtyModalOnly(row)) return;
        const input = getRowQtyInput(row);
        if (!input || input.disabled) return;
        const cur = readQty(input);
        const next = minusBtn ? Math.max(0, cur - 1) : cur + 1;
        reconcileRowQuantitySelection(row, next);
        return;
      }

      if (addBtn instanceof HTMLButtonElement && details.contains(addBtn)) {
        e.preventDefault();
        e.stopPropagation();
        if (addBtn.disabled || addBtn.dataset.addState === 'loading') return;
        addSelectedToCart(panel, /** @type {HTMLElement} */ (details));
      }
    });

    const onQtyInput = (e) => {
      const input =
        e.target instanceof HTMLInputElement && e.target.matches('[data-details-qty-input]')
          ? e.target
          : null;
      if (!input) return;
      const row = input.closest('[data-details-variant-row]');
      if (!(row instanceof HTMLElement) || !isRowAvailable(row)) {
        writeQty(input, 0, { silent: true });
        if (row instanceof HTMLElement) {
          clearRowWeight(row);
          syncBulkMap(panel, row);
        }
        notifyQtyUpdated(/** @type {HTMLElement} */ (details));
        return;
      }
      if (isRowQtyModalOnly(row)) return;
      const sanitized = readQty(input);
      reconcileRowQuantitySelection(row, sanitized);
    };

    details.addEventListener('input', onQtyInput);
    details.addEventListener('change', onQtyInput);
    details.addEventListener('blur', onQtyInput, true);

    details.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (row instanceof HTMLElement) {
        syncRowMinusState(row);
        syncWeightButtonState(row);
      }
    });
    updateAddSelectedState(details);
    updateDetailsSummary(details);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} open
   */
  function setPanelOpen(item, open) {
    const panel = /** @type {HTMLElement | null} */ (item.querySelector(PANEL_SEL));
    const row = /** @type {HTMLElement | null} */ (item.querySelector(ROW_SEL));
    const btn = /** @type {HTMLButtonElement | null} */ (item.querySelector(TOGGLE_SEL));
    const textEl = btn?.querySelector('[data-plp-details-toggle-text]');

    if (!panel || !btn) return;

    if (open) {
      clearCloseTimer(panel);
      bindPanel(panel);
      panel.hidden = false;
      panel.classList.add('is-open');
      const details = panel.querySelector('[data-plp-expanded-details]');
      if (details instanceof HTMLElement) {
        resetVariantTable(details);
        hydrateDetailsWmsAvailability(details);
      }
      requestAnimationFrame(() => {
        panel.style.setProperty('--plp-details-height', `${panel.scrollHeight}px`);
      });
      row?.classList.add('inventory-product-row--expanded');
      btn.setAttribute('aria-expanded', 'true');
      if (textEl) textEl.textContent = btn.dataset.labelClose || 'Close';
    } else {
      const details = panel.querySelector('[data-plp-expanded-details]');
      if (details instanceof HTMLElement) closeWeightModal(details);
      panel.classList.remove('is-open');
      panel.style.removeProperty('--plp-details-height');
      row?.classList.remove('inventory-product-row--expanded');
      btn.setAttribute('aria-expanded', 'false');
      if (textEl) textEl.textContent = btn.dataset.labelOpen || 'View Details';
      clearCloseTimer(panel);
      const timer = window.setTimeout(() => {
        if (!panel.classList.contains('is-open')) {
          panel.hidden = true;
        }
        delete panel.dataset.closeTimerId;
      }, 380);
      panel.dataset.closeTimerId = String(timer);
    }
  }

  /** @param {HTMLElement} host @param {HTMLElement | null} exceptItem */
  function closeAllExcept(host, exceptItem) {
    host.querySelectorAll(ITEM_SEL).forEach((item) => {
      if (exceptItem && item === exceptItem) return;
      setPanelOpen(/** @type {HTMLElement} */ (item), false);
    });
  }

  /**
   * Clicks on these controls inside an order-sheet row should keep their normal behavior
   * (qty, checkbox, add to cart) instead of toggling the details panel.
   * @param {Element} target
   */
  function isOrderSheetRowInteractive(target) {
    return Boolean(
      target.closest(
        [
          'input',
          'select',
          'textarea',
          'label',
          'product-form-component',
          '[data-inventory-row-select]',
          '[data-quantity-minus]',
          '[data-quantity-plus]',
          '[data-inv-qty-adj]',
          '[data-quantity-input]',
          '[data-add-to-cart]',
          '.inventory-product-row__select',
          '.inventory-product-row__qty',
          '.inventory-product-row__add-btn',
          '.inventory-product-row__form',
        ].join(', ')
      )
    );
  }

  function bindDocumentToggle() {
    if (documentToggleBound) return;
    documentToggleBound = true;

    // Whole order-sheet card click opens/closes the details panel (not PDP navigation).
    document.addEventListener(
      'click',
      (e) => {
        if (!(e.target instanceof Element)) return;

        // Never intercept interactions inside an already-open details panel.
        if (e.target.closest(PANEL_SEL)) return;

        const item = /** @type {HTMLElement | null} */ (e.target.closest(ITEM_SEL));
        if (!item) return;

        const host = /** @type {HTMLElement | null} */ (item.closest('.plp-view-host'));
        if (!host || host.dataset.plpEnableInventory !== 'true') return;

        const row = /** @type {HTMLElement | null} */ (
          e.target.closest(ROW_SEL) || item.querySelector(ROW_SEL)
        );
        if (!row || !item.contains(row)) return;

        const toggleBtn = /** @type {HTMLButtonElement | null} */ (item.querySelector(TOGGLE_SEL));
        if (!toggleBtn) return;

        const clickedToggle = Boolean(e.target.closest(TOGGLE_SEL));
        if (!clickedToggle && isOrderSheetRowInteractive(e.target)) return;

        // Block PDP links / default navigation from image + title clicks.
        e.preventDefault();
        e.stopPropagation();

        let rowHandle =
          row.getAttribute('data-product-handle') ||
          item.getAttribute('data-product-handle') ||
          '';
        if (!rowHandle) {
          const gridItem = item.closest('.plp-product-grid__item, .wizzy-result-product');
          const link = gridItem
            ? gridItem.querySelector('a[href*="/products/"]')
            : item.querySelector('a[href*="/products/"]');
          const href = link instanceof HTMLAnchorElement ? link.getAttribute('href') || '' : '';
          const match = href.match(/\/products\/([^/?#]+)/);
          if (match && match[1]) {
            rowHandle = decodeURIComponent(match[1]);
            row.setAttribute('data-product-handle', rowHandle);
            item.setAttribute('data-product-handle', rowHandle);
          }
        }
        const qv = /** @type {any} */ (window).__aoneQuickViewModalController;
        if (rowHandle && qv && typeof qv.open === 'function') {
          const callerProduct = parseDetailsProductJson(item) || parseDetailsProductJson(row);
          closeAllExcept(host, null);
          setPanelOpen(item, false);
          qv.open({ handle: rowHandle, product: callerProduct });
          return;
        }

        const expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        if (!expanded && !allowMultiple(host)) {
          closeAllExcept(host, item);
        }
        setPanelOpen(item, !expanded);
      },
      true
    );

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-order-sheet-weight-modal]:not([hidden])').forEach((modal) => {
        if (!(modal instanceof HTMLElement)) return;
        const state = getWeightModalStateFromModal(modal);
        if (!state) return;
        closeWeightModal(state.detailsRoot);
      });
    });
  }

  /** @param {HTMLElement} host */
  function bindHost(host) {
    hostControllers.get(host)?.abort();
    const ac = new AbortController();
    hostControllers.set(host, ac);

    host.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        if (host.dataset.plpEnableInventory !== 'true') return;
        const grid = host.querySelector('[data-plp-layout="inventory"], [data-plp-view="inventory"]');
        if (!grid) return;
        closeAllExcept(host, null);
      },
      { signal: ac.signal }
    );
  }

  function init() {
    bindDocumentToggle();
    document.querySelectorAll('.plp-view-host').forEach((host) => {
      if (!(host instanceof HTMLElement)) return;
      if (host.dataset.plpEnableInventory !== 'true') return;
      bindHost(host);
    });
  }

  function rehydrateOpenDetailsFromCart(event) {
    const detail = event && event.detail ? event.detail : null;
    const availabilityApi = getWmsAvailabilityApi();
    const cartReady =
      availabilityApi && typeof availabilityApi.isCartStateReady === 'function'
        ? availabilityApi.isCartStateReady()
        : true;
    const loading =
      (detail && (detail.loading === true || detail.cartReady === false)) ||
      !cartReady;

    document
      .querySelectorAll(
        '[data-plp-expanded-details], [data-order-sheet-expanded-details], [data-quick-view-details], [data-premium-pdp-details]'
      )
      .forEach((detailsRoot) => {
        if (!(detailsRoot instanceof HTMLElement)) return;
        if (detailsRoot.hidden) return;
        // Quick View listens on aone:cart-inventory-updated and rehydrates itself.
        if (detailsRoot.hasAttribute('data-quick-view-details')) return;
        if (loading) {
          detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
            if (!(row instanceof HTMLElement)) return;
            setRowWmsStatus(row, 'loading', 0);
            syncRowWeightActionLabel(row, { isLoading: true });
            syncRowMinusState(row);
            syncWeightButtonState(row);
          });
          updateAddSelectedState(detailsRoot);
          return;
        }
        hydrateDetailsWmsAvailability(detailsRoot).catch(() => {});
      });
  }

  if (document.documentElement.dataset.aonePlpCartInventoryBound !== 'true') {
    document.documentElement.dataset.aonePlpCartInventoryBound = 'true';
    const availabilityApi = getWmsAvailabilityApi();
    const eventName =
      (availabilityApi && availabilityApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
    document.addEventListener(eventName, (event) => {
      rehydrateOpenDetailsFromCart(event);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => queueMicrotask(init));
  document.addEventListener(FILTER_EVT, () => {
    document.querySelectorAll('.plp-view-host').forEach((h) => {
      if (h instanceof HTMLElement) hostControllers.get(h)?.abort();
    });
    queueMicrotask(() => setTimeout(init, 0));
  });

  window.plpVariantDetails = {
    bindPanel,
    openImageViewer,
    bindChainMatrixStickyHeaderSync,
    syncChainMatrixStickyHeaderHeights,
    reconcileRowQuantitySelection,
    getSelectableWeightEntriesForSku,
  };
})();
